import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDeliveryTabShortcut from "../pedidos/useDeliveryTabShortcut";
import useAccessControl from "../context/useAccessControl";
import { openWhatsAppEmbeddedSignup } from "../../services/whatsapp/embeddedSignup";
import useMetaSdk from "../../services/whatsapp/useMetaSdk";
import OperationalWorkspaceSwitcher from "../common/OperationalWorkspaceSwitcher";
import WePedidosAdminPanel from "../pedidos/WePedidosAdminPanel";
import { WE_PEDIDOS_AVAILABLE, WE_PEDIDOS_UNAVAILABLE_MESSAGE } from "../../services/local/wePedidosAvailability";
import WhatsAppNumberConnection from "../pedidos/WhatsAppNumberConnection";
import { WE_PEDIDOS_LOGO_URL as wePedidosLogo } from "../../services/local/institutionalBranding";
import { getBranchMenuSnapshot } from "../../services/supabase/menuService";
import { getRestaurantProfile } from "../../services/local/restaurantProfile";
import {
  getIngredientStockValidationMessage,
  validateIngredientStock,
} from "../../services/local/ingredientStock";
import { listBranchIngredientStock } from "../../services/supabase/branchStockService";
import { applyOperationalOrderStock } from "../../services/supabase/orderStockService";
import { refundApprovedDigitalPayment } from "../../services/supabase/kioskDigitalPaymentsService";
import {
  PHASE1_REQUIRED_MIGRATION,
  EXTERNAL_ORDERS_UPDATED_EVENT,
  assignExternalOrderDriver,
  buildExternalDeliveryPublicUrl,
  calculateExternalCartSummary,
  calculateExternalCashChange,
  completeExternalWhatsAppEmbeddedSignup,
  createExternalWhatsAppTemplate,
  createExternalOrder,
  dispatchExternalOrderToKitchen,
  formatExternalPhone,
  getExternalOrderStatusMeta,
  getExternalPaymentMethodLabel,
  getExternalPaymentStatusLabel,
  getExternalSourceChannelLabel,
  hasExternalDeliveryDriverAssignment,
  isExternalDeliveryAssignmentAvailable,
  getExternalWhatsAppSetup,
  getExternalWhatsAppEmbeddedSignupConfig,
  getExternalDeliveryCoverage,
  listExternalCustomers,
  listExternalDeliveryDistanceRates,
  listExternalDeliveryZones,
  listExternalOrders,
  listExternalSalesChannels,
  listExternalWhatsAppWebhookEvents,
  offerExternalOrderToWeRiders,
  recordExternalOrderPayment,
  releaseExpiredWeRiderAssignment,
  removeExternalCustomer,
  removeExternalDeliveryDistanceRate,
  resolveExternalDeliveryDistanceRate,
  resolveExternalOrderCustomerRequest,
  saveExternalCustomer,
  saveExternalCustomerAddress,
  saveExternalDeliveryCoverage,
  saveExternalDeliveryDistanceRate,
  saveExternalDeliveryZone,
  saveExternalSalesChannel,
  saveExternalWhatsAppSetup,
  sendExternalWhatsAppTestMessage,
  subscribeToExternalOrderChanges,
  transitionExternalOrderStatus,
} from "../../services/supabase/externalOrdersService";
import {
  cancelLocalWeRiderDelivery,
  getLocalWeRiderAssignment,
  isUsingLocalWeRiderAuth,
  publishLocalWeRiderOffer,
  releaseExpiredLocalWeRiderAssignment,
  removeLocalWeRiderOffer,
  subscribeToLocalWeRiderUpdates,
} from "../../services/supabase/weRiderService";

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});
const EXTERNAL_ORDER_SYNC_INTERVAL_MS = 4000;
const DELIVERY_INITIAL_LOAD_TIMEOUT_MS = 12000;

const withDeliveryLoadTimeout = async (request, label) => {
  let timeoutId;
  try {
    return await Promise.race([
      request,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(new Error(`${label} demoró demasiado en responder.`));
        }, DELIVERY_INITIAL_LOAD_TIMEOUT_MS);
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const getExternalItemSelectionDetails = (item) => {
  const details = Array.isArray(item?.selection_details)
    ? item.selection_details
    : Array.isArray(item?.selectionDetails)
      ? item.selectionDetails
      : [];

  return details.map((detail) => String(detail || "").trim()).filter(Boolean);
};

const formatExternalOrderAddress = (address) => {
  if (!address) return "Sin direccion cargada";

  const main = [address.street, address.street_number || address.streetNumber]
    .filter(Boolean)
    .join(" ");
  const location = [
    address.floor,
    address.unit,
    address.neighborhood,
    address.city,
    address.postal_code || address.postalCode,
  ]
    .filter(Boolean)
    .join(" | ");
  return [main, location].filter(Boolean).join(" - ") || "Sin direccion cargada";
};

const formatWebhookActivityTime = (value) => {
  if (!value) return "Sin actividad todavia";

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Sin actividad todavia"
    : new Intl.DateTimeFormat("es-AR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
};

const getWhatsAppWebhookActivityCopy = (event) => {
  if (event?.event_type === "send_error") {
    return {
      label: "Meta rechazó la respuesta automática",
      detail:
        event.error_message ||
        "Meta no informó el motivo. Volvé a enviar Hola y actualizá la actividad.",
      tone: "error",
    };
  }

  return {
    label:
      event?.direction === "inbound"
        ? "Mensaje recibido desde el cliente"
        : "Respuesta enviada por Mozzo",
    detail: "",
    tone: "default",
  };
};

const splitZoneCoverage = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const formatZoneCoverage = (value) =>
  (Array.isArray(value) ? value : []).filter(Boolean).join(", ");

const ORDER_TABS = [
  { id: "active", label: "Pedidos activos" },
  { id: "history", label: "Historial" },
  { id: "new", label: "Nuevo pedido" },
  { id: "customers", label: "Clientes" },
  { id: "delivery", label: "Delivery y zonas" },
  { id: "channels", label: "Canales de venta" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "we", label: "We! pedidos", icon: wePedidosLogo },
  { id: "settings", label: "Configuración" },
];

const CHANNEL_CARDS = [
  {
    code: "manual",
    title: "Carga manual",
    detail: "El equipo registra pedidos por teléfono, mostrador o redes sin perder trazabilidad.",
  },
  {
    code: "menu_web",
    title: "Menú público",
    detail: "Preparado para una URL externa de pedidos con retiro, delivery y pago al finalizar.",
  },
  {
    code: "chatbot",
    title: "Chatbot",
    detail: "Preparado para conversaciones guiadas que arman el pedido sin inventar precios.",
  },
  {
    code: "whatsapp",
    title: "WhatsApp",
    detail: "En Fase 1 queda en modo simulación segura: todavía no conecta Meta ni guarda claves.",
  },
];

const EMPTY_CUSTOMER = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  marketingOptIn: false,
  internalNotes: "",
};

const EMPTY_ADDRESS = {
  label: "Principal",
  street: "",
  streetNumber: "",
  floor: "",
  unit: "",
  neighborhood: "",
  city: "",
  province: "",
  postalCode: "",
  reference: "",
  deliveryDistanceMeters: null,
  deliveryRouteDurationSeconds: null,
  deliveryFeeOverride: null,
};

const EMPTY_ZONE = {
  name: "",
  neighborhoods: "",
  cities: "",
  flatFee: "",
  minOrder: "",
  freeFrom: "",
  estimatedMinutes: "35",
  active: true,
};

const EMPTY_DISTANCE_RATE = {
  minDistance: "0",
  maxDistance: "",
  unit: "m",
  fee: "",
  estimatedMinutes: "35",
  active: true,
};

const formatDistanceRateValue = (meters, unit = "m") => {
  const safeMeters = Math.max(0, Number(meters || 0));
  if (unit === "km") {
    return `${(safeMeters / 1000).toLocaleString("es-AR", {
      maximumFractionDigits: 2,
    })} km`;
  }
  return `${Math.round(safeMeters).toLocaleString("es-AR")} m`;
};

const EMPTY_WHATSAPP = {
  active: false,
  displayName: "",
  businessPhone: "",
  deliveryPublicToken: "",
  deliveryMenuEnabled: true,
  chatbotEnabled: true,
  registrationRequired: true,
  webhookStatus: "not_configured",
  welcomeMessage: "",
  unavailableMessage: "",
  handoffMessage: "",
};

// IDs returned by Meta are kept in Supabase. The browser only retains the
// operational settings a restaurant can edit, never the connection payload.
const restoreWhatsAppForm = (value = {}) => ({
  active: value?.active === true,
  displayName: String(value?.displayName || ""),
  businessPhone: String(value?.businessPhone || ""),
  deliveryPublicToken: String(value?.deliveryPublicToken || ""),
  deliveryMenuEnabled: value?.deliveryMenuEnabled !== false,
  chatbotEnabled: value?.chatbotEnabled !== false,
  registrationRequired: value?.registrationRequired !== false,
  webhookStatus: String(value?.webhookStatus || "not_configured"),
  welcomeMessage: String(value?.welcomeMessage || ""),
  unavailableMessage: String(value?.unavailableMessage || ""),
  handoffMessage: String(value?.handoffMessage || ""),
});


const getDeliveryWorkspaceDraftKey = (branchId) =>
  `mozzo:delivery-workspace-draft:${String(branchId || "").trim()}`;

const readDeliveryWorkspaceDraft = (branchId) => {
  if (!branchId || typeof window === "undefined") return null;

  try {
    const rawDraft = window.sessionStorage.getItem(getDeliveryWorkspaceDraftKey(branchId));
    return rawDraft ? JSON.parse(rawDraft) : null;
  } catch {
    return null;
  }
};

const saveDeliveryWorkspaceDraft = (branchId, draft) => {
  if (!branchId || typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      getDeliveryWorkspaceDraftKey(branchId),
      JSON.stringify(draft)
    );
  } catch {
    // A blocked or full session storage must not interrupt the delivery operation.
  }
};

const isKnownOrderTab = (tabId) => (tabId !== "we" || WE_PEDIDOS_AVAILABLE) && ORDER_TABS.some((tab) => tab.id === tabId);

const mergeLocalWeRiderAssignment = (order) => {
  const localAssignment = getLocalWeRiderAssignment(order?.id);
  if (!localAssignment) return order;

  const synchronizedOrderStatus = {
    driver_assigned: "assigned_to_driver",
    arriving_pickup: "assigned_to_driver",
    ready_for_pickup: "assigned_to_driver",
    arrived_pickup: "assigned_to_driver",
    picked_up: "out_for_delivery",
    in_transit: "out_for_delivery",
    delivered: "delivered",
    cancelled: "cancelled",
  }[localAssignment.status];

  return {
    ...order,
    status: synchronizedOrderStatus || order.status,
    delivery_assignment: localAssignment,
    driver_name: localAssignment.driver_name || order.driver_name || "",
    driver_phone: localAssignment.driver_phone || order.driver_phone || "",
  };
};

const TERMINAL_EXTERNAL_ORDER_STATUSES = new Set([
  "delivered",
  "picked_up",
  "cancelled",
  "rejected",
  "refunded",
]);

const PICKUP_SECURITY_STATUSES = new Set([
  "driver_assigned",
  "arriving_pickup",
  "ready_for_pickup",
  "arrived_pickup",
]);

const isPickupWindowExpired = (order) => {
  const assignment = order?.delivery_assignment;
  const deadline = assignment?.pickup_deadline_at;
  return Boolean(
    assignment?.driver_id &&
      ["driver_assigned", "arriving_pickup", "ready_for_pickup"].includes(
        assignment?.status
      ) &&
      deadline &&
      new Date(deadline).getTime() <= Date.now()
  );
};

const formatPickupDeadline = (value) => {
  if (!value) return "Sin límite informado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin límite informado";
  return date.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const mergeOrdersWithRiderState = (activeOrders = [], historyOrders = []) => {
  const synchronizedActive = activeOrders.map(mergeLocalWeRiderAssignment);
  const synchronizedHistory = historyOrders.map(mergeLocalWeRiderAssignment);
  const active = synchronizedActive.filter(
    (order) => !TERMINAL_EXTERNAL_ORDER_STATUSES.has(order.status)
  );
  const historyById = new Map(
    [...synchronizedHistory, ...synchronizedActive.filter((order) =>
      TERMINAL_EXTERNAL_ORDER_STATUSES.has(order.status)
    )].map((order) => [order.id, order])
  );
  const history = [...historyById.values()].sort(
    (left, right) =>
      new Date(right?.created_at || 0).getTime() -
      new Date(left?.created_at || 0).getTime()
  );
  return { active, history };
};

const isRiderControlledDelivery = (order) => {
  const assignment = order?.delivery_assignment;
  return Boolean(
    assignment?.driver_id &&
      !isPickupWindowExpired(order) &&
      !["delivered", "cancelled"].includes(assignment?.status)
  );
};

const statusNextActions = (order, { kitchenMode = "manual" } = {}) => {
  switch (order?.status) {
    case "received":
    case "pending_confirmation":
    case "pending_payment":
      return [{ status: "preparing", label: "Enviar a Cocina" }];
    case "confirmed":
      return [{ status: "preparing", label: "Empezar preparación" }];
    case "preparing":
      {
        const deliveryAssignmentAction =
          order.fulfillment_type === "delivery" &&
          isExternalDeliveryAssignmentAvailable(order) &&
          !hasExternalDeliveryDriverAssignment(order)
            ? [{
                status: "assigned_to_driver",
                label:
                  order?.delivery_assignment?.status === "searching_driver"
                    ? "Cambiar asignación"
                    : "Asignar repartidor",
              }]
            : [];
        if (kitchenMode === "screen") return deliveryAssignmentAction;
        return [
          {
            status:
              order.fulfillment_type === "delivery"
                ? "ready_for_delivery"
                : "ready_for_pickup",
            label: "Marcar listo",
          },
          ...deliveryAssignmentAction,
        ];
      }
    case "ready_for_delivery":
      if (isRiderControlledDelivery(order)) return [];
      return [{
        status: "assigned_to_driver",
        label: order?.delivery_assignment?.status === "searching_driver"
          ? "Cambiar asignación"
          : "Asignar repartidor",
      }];
    case "assigned_to_driver":
      if (isPickupWindowExpired(order)) {
        return [{ status: "assigned_to_driver", label: "Reasignar Rider" }];
      }
      if (isRiderControlledDelivery(order)) return [];
      return [{ status: "out_for_delivery", label: "En camino" }];
    case "out_for_delivery":
      if (isRiderControlledDelivery(order)) return [];
      return [{ status: "delivered", label: "Confirmar entrega" }];
    case "ready_for_pickup":
      return [{ status: "picked_up", label: "Confirmar retiro" }];
    default:
      return [];
  }
};

const getDeliveryAssignmentLabel = (order) => {
  const assignment = order?.delivery_assignment;
  const statusLabel = {
    waiting_restaurant: "Esperando confirmación",
    searching_driver: "Buscando Rider en We! pedidos",
    driver_assigned: "Repartidor asignado",
    arriving_pickup: "Yendo al restaurante",
    ready_for_pickup: "Esperando retiro",
    arrived_pickup: "En el restaurante, validando retiro",
    picked_up: "Pedido retirado",
    in_transit: "En camino al cliente",
    delivered: "Entrega completada",
    cancelled: "Entrega cancelada",
  }[assignment?.status];
  const driverName =
    assignment?.driver_name || order?.driver_name || "";
  if (isPickupWindowExpired(order)) {
    return driverName
      ? `${driverName} · Tiempo de llegada vencido`
      : "Tiempo de llegada vencido";
  }
  if (driverName && statusLabel) return `${driverName} · ${statusLabel}`;
  if (driverName) return driverName;
  if (statusLabel) return statusLabel;
  return "Sin asignar";
};

function PickupVerificationNotice({ order, compact = false }) {
  const assignment = order?.delivery_assignment;
  if (
    !assignment?.driver_id ||
    !PICKUP_SECURITY_STATUSES.has(assignment?.status)
  ) {
    return null;
  }

  const expired = isPickupWindowExpired(order);
  const arrived = assignment.status === "arrived_pickup";
  const locked = Boolean(assignment.pickup_verification_locked_at);
  const attempts = Number(assignment.pickup_verification_attempts || 0);

  return (
    <div
      style={{
        ...styles.pickupSecurity,
        ...(compact ? styles.pickupSecurityCompact : {}),
        ...(expired || locked ? styles.pickupSecurityDanger : {}),
      }}
    >
      <div>
        <span style={styles.pickupSecurityLabel}>
          {expired
            ? "Tiempo de llegada vencido"
            : locked
              ? "Código bloqueado"
              : arrived
                ? "Rider esperando validación"
                : "Código seguro de retiro"}
        </span>
        {!expired && !locked && assignment.pickup_verification_code ? (
          <strong style={styles.pickupSecurityCode}>
            {assignment.pickup_verification_code}
          </strong>
        ) : !expired && !locked ? (
          <strong style={styles.pickupSecurityPending}>
            Generando codigo...
          </strong>
        ) : null}
      </div>
      <small style={styles.pickupSecurityText}>
        {expired
          ? "Reasigna el viaje antes de entregar el pedido."
          : locked
            ? "El Rider agotó los intentos. Reasigna el viaje."
            : !assignment.pickup_verification_code
              ? "MozzoApp esta recuperando el codigo seguro de este retiro."
            : arrived
              ? `El Rider llegó dentro del plazo. Dile este código para autorizar el retiro.${
                  attempts ? ` Intentos usados: ${attempts}/5.` : ""
                }`
              : `Muéstralo únicamente cuando el Rider llegue. Debe arribar antes de las ${formatPickupDeadline(
                  assignment.pickup_deadline_at
                )}.`}
      </small>
    </div>
  );
}

const getProductPrice = (product) => {
  if (!product) return 0;
  return Number(product.precio ?? product.price ?? product.precioSugerido ?? 0);
};

const buildProductStockConsumptions = (product) =>
  (Array.isArray(product?.ingredients) ? product.ingredients : [])
    .map((ingredient) => ({
      ingredientId: ingredient.ingredientId || ingredient.id || "",
      ingredientName: ingredient.nombre || ingredient.name || "Ingrediente",
      quantity: Math.max(
        0,
        Number(
          ingredient.consumptionQuantity ??
            ingredient.stockQuantity ??
            ingredient.consumptionBaseQuantity ??
            0
        )
      ),
      unit: ingredient.consumptionUnit || ingredient.unidad || ingredient.unit || "un",
    }))
    .filter((ingredient) => ingredient.ingredientId && ingredient.quantity > 0);

const getStockConsumptionsAvailability = (
  consumptions,
  stockMap,
  requestedQuantity = 1
) => {
  const safeRequestedQuantity = Math.max(1, Number(requestedQuantity || 1));
  const scaledConsumptions = (Array.isArray(consumptions) ? consumptions : []).map(
    (consumption) => ({
      ...consumption,
      quantity: Number(consumption?.quantity || 0) * safeRequestedQuantity,
    })
  );
  const validation = validateIngredientStock(null, scaledConsumptions, {
    stockMap,
    allowMissingStockRecords: false,
  });

  if (validation.ok) {
    return { available: true, reason: "" };
  }

  return {
    available: false,
    reason: getIngredientStockValidationMessage(validation, {
      unavailablePrefix: "Sin stock suficiente de",
      incompatiblePrefix: "La unidad cargada no coincide con la receta de",
      fallbackMessage: "No hay stock suficiente para agregar este producto.",
    }),
  };
};

const getProductStockAvailability = (product, stockEnabled, stockMap, requestedQuantity = 1) => {
  if (!stockEnabled) {
    return { available: true, reason: "" };
  }

  return getStockConsumptionsAvailability(
    buildProductStockConsumptions(product),
    stockMap,
    requestedQuantity
  );
};

const getCartItemStockAvailability = (item, stockEnabled, stockMap, requestedQuantity) => {
  if (!stockEnabled) return { available: true, reason: "" };

  return getStockConsumptionsAvailability(
    item?.stockConsumptions || [],
    stockMap,
    requestedQuantity
  );
};

const buildRequestKey = () =>
  `manual-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const formatOrderDate = (value) =>
  new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));

const InputField = ({ label, children, hint = "" }) => (
  <label style={styles.field}>
    <span style={styles.fieldLabel}>{label}</span>
    {children}
    {hint ? <span style={styles.fieldHint}>{hint}</span> : null}
  </label>
);

const PermissionEmptyState = ({ roleLabel, children }) => (
  <div style={styles.permissionEmpty}>
    <strong>Acceso de {roleLabel}</strong>
    <span>{children}</span>
  </div>
);

export default function Pedidos() {
  const {
    currentBranch,
    currentCompany,
    currentAccessRoleLabel,
    canPerformAction,
  } = useAccessControl();
  const branch = currentBranch;
  const branchSnapshotRef = useRef(branch);
  branchSnapshotRef.current = branch;
  const branchId = branch?.id || "";
  const activeKitchenMode = getRestaurantProfile(branch)?.kitchenMode || "manual";
  const companyId = currentCompany?.id || "";
  const canViewOrders = canPerformAction("view_external_orders");
  const canCreateOrders = canPerformAction("create_external_orders");
  const canEditOrders = canPerformAction("edit_external_orders");
  const canChangeStatus = canPerformAction("change_external_order_status");
  const canDispatchToKitchen = canPerformAction("dispatch_external_order_to_kitchen");
  const canCancelOrders = canPerformAction("cancel_external_orders");
  const canRefundOrders = canPerformAction("refund_orders");
  const canAssignDelivery = canPerformAction("assign_external_delivery");
  const canViewCustomers = canPerformAction("view_customers");
  const canManageCustomers = canPerformAction("manage_customers");
  const canManageDelivery = canPerformAction("manage_delivery");
  const canManageChannels = canPerformAction("manage_sales_channels");
  const canManageWhatsApp = canPerformAction("manage_whatsapp");

  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [orders, setOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [deliveryDistanceRates, setDeliveryDistanceRates] = useState([]);
  const [distanceRatesAvailable, setDistanceRatesAvailable] = useState(true);
  const [deliveryCoverage, setDeliveryCoverage] = useState({
    available: true,
    radiusEnabled: false,
    maxDistanceKm: "",
    originConfigured: false,
    originReference: "",
  });
  const [salesChannels, setSalesChannels] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [stockEnabled, setStockEnabled] = useState(false);
  const [whatsAppSetup, setWhatsAppSetup] = useState(null);
  const [whatsAppWebhookEvents, setWhatsAppWebhookEvents] = useState([]);
  const [whatsAppEmbeddedSignupConfig, setWhatsAppEmbeddedSignupConfig] = useState(null);
  const [whatsAppConfigAttempt, setWhatsAppConfigAttempt] = useState(0);
  const whatsAppSdk = useMetaSdk(whatsAppEmbeddedSignupConfig?.ready === true);
  const [loadingWhatsAppConnection, setLoadingWhatsAppConnection] = useState(false);
  const [whatsAppConnectionPhase, setWhatsAppConnectionPhase] = useState("");
  const [whatsAppTestRecipient, setWhatsAppTestRecipient] = useState("");
  const [whatsAppTemplateName, setWhatsAppTemplateName] = useState(
    () => `mozzo_revision_${Date.now()}`
  );
  const [whatsAppTemplateBody, setWhatsAppTemplateBody] = useState(
    "Hola, tenemos una actualizacion sobre tu pedido. Ingresa a MozzoApp para ver el estado."
  );
  const [whatsAppTemplateResult, setWhatsAppTemplateResult] = useState(null);
  const [menuSnapshot, setMenuSnapshot] = useState({ categories: [], products: [] });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [driverAssignmentOrder, setDriverAssignmentOrder] = useState(null);
  const [driverAssignmentError, setDriverAssignmentError] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [orderCustomer, setOrderCustomer] = useState(EMPTY_CUSTOMER);
  const [orderAddress, setOrderAddress] = useState(EMPTY_ADDRESS);
  const [fulfillmentType, setFulfillmentType] = useState("pickup");
  const [deliveryZoneId, setDeliveryZoneId] = useState("");
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER);
  const [customerAddressForm, setCustomerAddressForm] = useState(EMPTY_ADDRESS);
  const [zoneForm, setZoneForm] = useState(EMPTY_ZONE);
  const [editingZoneId, setEditingZoneId] = useState("");
  const [distanceRateForm, setDistanceRateForm] = useState(EMPTY_DISTANCE_RATE);
  const [editingDistanceRateId, setEditingDistanceRateId] = useState("");
  const [whatsappForm, setWhatsappForm] = useState(EMPTY_WHATSAPP);
  const restoredDraftRef = useRef(null);
  const activeOrdersRefreshInFlightRef = useRef(false);
  const activeOrdersRefreshPendingRef = useRef(false);
  const [restoredBranchId, setRestoredBranchId] = useState("");

  useEffect(() => {
    if (!branch?.id) return;

    // This route is unmounted when the operator goes back to Salon, so recover its local work per branch.
    const savedDraft = readDeliveryWorkspaceDraft(branch.id);
    restoredDraftRef.current = savedDraft ? { branchId: branch.id, draft: savedDraft } : null;

    setActiveTab(isKnownOrderTab(savedDraft?.activeTab) ? savedDraft.activeTab : "active");
    setHistoryDateFrom(savedDraft?.historyDateFrom || "");
    setHistoryDateTo(savedDraft?.historyDateTo || "");
    setCustomerSearch(savedDraft?.customerSearch || "");
    setProductSearch(savedDraft?.productSearch || "");
    setSelectedCategoryId(savedDraft?.selectedCategoryId || "");
    setCartItems(Array.isArray(savedDraft?.cartItems) ? savedDraft.cartItems : []);
    setOrderCustomer({ ...EMPTY_CUSTOMER, ...(savedDraft?.orderCustomer || {}) });
    setOrderAddress({ ...EMPTY_ADDRESS, ...(savedDraft?.orderAddress || {}) });
    setFulfillmentType(savedDraft?.fulfillmentType === "delivery" ? "delivery" : "pickup");
    setDeliveryZoneId(savedDraft?.deliveryZoneId || "");
    setDeliveryFeeOverride(savedDraft?.deliveryFeeOverride ?? "");
    setPaymentMethod(savedDraft?.paymentMethod || "cash");
    setCashReceived(savedDraft?.cashReceived || "");
    setCustomerNotes(savedDraft?.customerNotes || "");
    setInternalNotes(savedDraft?.internalNotes || "");
    setCustomerForm({ ...EMPTY_CUSTOMER, ...(savedDraft?.customerForm || {}) });
    setCustomerAddressForm({ ...EMPTY_ADDRESS, ...(savedDraft?.customerAddressForm || {}) });
    setZoneForm({ ...EMPTY_ZONE, ...(savedDraft?.zoneForm || {}) });
    setEditingZoneId(savedDraft?.editingZoneId || "");
    setDistanceRateForm({
      ...EMPTY_DISTANCE_RATE,
      ...(savedDraft?.distanceRateForm || {}),
    });
    setEditingDistanceRateId(savedDraft?.editingDistanceRateId || "");
    setWhatsappForm(restoreWhatsAppForm(savedDraft?.whatsappForm));
    setRestoredBranchId(branch.id);
  }, [branch?.id]);

  useDeliveryTabShortcut(setActiveTab, isKnownOrderTab);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      const activeBranch = branchSnapshotRef.current;
      if (!branchId || !activeBranch?.id || !companyId) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      setError("");

      try {
        const profile = getRestaurantProfile(activeBranch);
        const shouldUseStock = profile?.stockEnabled !== false;
        const optionalLoadFailures = [];
        const loadOptional = (request, fallback, label) =>
          withDeliveryLoadTimeout(request, label).catch(() => {
            optionalLoadFailures.push(label);
            return fallback;
          });
        const [nextOrders, nextHistory, nextCustomers, nextZones, nextDistanceRatesState, nextCoverage, nextChannels, nextWhatsApp, nextWhatsAppEvents, nextMenu, nextStock] =
          await Promise.all([
            withDeliveryLoadTimeout(
              listExternalOrders({ branchId: activeBranch.id, mode: "active" }),
              "Los pedidos activos"
            ),
            loadOptional(
              listExternalOrders({
                branchId: activeBranch.id,
                mode: "history",
                fromDate: historyDateFrom,
                toDate: historyDateTo,
              }),
              [],
              "El historial"
            ),
            loadOptional(listExternalCustomers({ companyId }), [], "Los clientes"),
            loadOptional(listExternalDeliveryZones(activeBranch.id), [], "Las zonas"),
            loadOptional(
              listExternalDeliveryDistanceRates(activeBranch.id),
              { rates: [], available: true },
              "Las tarifas"
            ),
            loadOptional(
              getExternalDeliveryCoverage(activeBranch.id),
              { radiusEnabled: false, maxDistanceKm: "", originReference: "" },
              "La cobertura"
            ),
            loadOptional(listExternalSalesChannels(activeBranch.id), [], "Los canales"),
            loadOptional(getExternalWhatsAppSetup(activeBranch.id), null, "WhatsApp"),
            loadOptional(
              listExternalWhatsAppWebhookEvents({ branchId: activeBranch.id }),
              [],
              "La actividad de WhatsApp"
            ),
            loadOptional(
              getBranchMenuSnapshot(activeBranch.id),
              { categories: [], products: [] },
              "El menú"
            ),
            shouldUseStock
              ? loadOptional(listBranchIngredientStock(activeBranch.id), {}, "El stock")
              : Promise.resolve({}),
          ]);
        const synchronizedOrders = mergeOrdersWithRiderState(
          nextOrders,
          nextHistory
        );
        setOrders(synchronizedOrders.active);
        setHistoryOrders(synchronizedOrders.history);
        setCustomers(nextCustomers);
        setDeliveryZones(nextZones);
        setDeliveryDistanceRates(nextDistanceRatesState.rates);
        setDistanceRatesAvailable(nextDistanceRatesState.available);
        setDeliveryCoverage(nextCoverage);
        setSalesChannels(nextChannels);
        setWhatsAppSetup(nextWhatsApp);
        setWhatsAppWebhookEvents(nextWhatsAppEvents);
        setMenuSnapshot(nextMenu);
        setStockEnabled(shouldUseStock);
        setStockMap(nextStock || {});
        const restoredWhatsAppForm =
          restoredDraftRef.current?.branchId === activeBranch.id
            ? restoredDraftRef.current.draft?.whatsappForm
            : null;
        setWhatsappForm(
          restoredWhatsAppForm
            ? restoreWhatsAppForm(restoredWhatsAppForm)
            : restoreWhatsAppForm({
                active: nextWhatsApp?.active === true,
                displayName: nextWhatsApp?.display_name || "",
                businessPhone: nextWhatsApp?.business_phone_e164 || "",
                deliveryPublicToken: nextWhatsApp?.delivery_public_token || "",
                deliveryMenuEnabled: nextWhatsApp?.delivery_menu_enabled !== false,
                chatbotEnabled: nextWhatsApp?.chatbot_enabled !== false,
                registrationRequired: nextWhatsApp?.registration_required !== false,
                webhookStatus: nextWhatsApp?.webhook_status || "not_configured",
                welcomeMessage: nextWhatsApp?.welcome_message || "",
                unavailableMessage: nextWhatsApp?.unavailable_message || "",
                handoffMessage: nextWhatsApp?.handoff_message || "",
              })
        );
        const preferredSelectedOrderId =
          restoredDraftRef.current?.branchId === activeBranch.id
            ? restoredDraftRef.current.draft?.selectedOrderId
            : "";
        setSelectedOrder((currentOrder) => {
          const orderId = currentOrder?.id || preferredSelectedOrderId;
          return synchronizedOrders.active.find((order) => order.id === orderId) || null;
        });
        if (optionalLoadFailures.length) {
          setNotice(
            "Los pedidos ya están disponibles. Algunos datos secundarios se actualizarán automáticamente."
          );
        }
      } catch (loadError) {
        setError(loadError.message || "No se pudo cargar el módulo Pedidos.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [branchId, companyId, historyDateFrom, historyDateTo]
  );

  const refreshActiveOrders = useCallback(async () => {
    if (!branch?.id) return;

    if (activeOrdersRefreshInFlightRef.current) {
      activeOrdersRefreshPendingRef.current = true;
      return;
    }

    activeOrdersRefreshInFlightRef.current = true;
    try {
      do {
        activeOrdersRefreshPendingRef.current = false;
        const nextOrders = await listExternalOrders({
          branchId: branch.id,
          mode: "active",
        });
        const synchronizedOrders = mergeOrdersWithRiderState(nextOrders, []);
        setOrders(synchronizedOrders.active);
        if (synchronizedOrders.history.length) {
          setHistoryOrders((currentHistory) =>
            mergeOrdersWithRiderState([], [
              ...currentHistory,
              ...synchronizedOrders.history,
            ]).history
          );
        }
        setSelectedOrder((currentOrder) =>
          currentOrder
            ? synchronizedOrders.active.find(
                (order) => order.id === currentOrder.id
              ) || null
            : null
        );
      } while (activeOrdersRefreshPendingRef.current);
    } finally {
      activeOrdersRefreshInFlightRef.current = false;
    }
  }, [branch?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!branch?.id) return undefined;

    const handleExternalOrdersUpdated = (event) => {
      if (event?.detail?.branchId !== branch.id) return;
      void refreshActiveOrders();
    };
    const unsubscribeRider = subscribeToLocalWeRiderUpdates(() => {
      void refreshActiveOrders();
    });
    const unsubscribeRemote = subscribeToExternalOrderChanges(branch.id, () => {
      void refreshActiveOrders();
    });

    window.addEventListener(EXTERNAL_ORDERS_UPDATED_EVENT, handleExternalOrdersUpdated);
    return () => {
      unsubscribeRider();
      unsubscribeRemote();
      window.removeEventListener(
        EXTERNAL_ORDERS_UPDATED_EVENT,
        handleExternalOrdersUpdated
      );
    };
  }, [branch?.id, refreshActiveOrders]);

  useEffect(() => {
    if (!branch?.id || activeTab !== "active") return undefined;

    const refresh = () => {
      void refreshActiveOrders().catch(() => {
        // A later refresh will recover from a temporary connection problem.
      });
    };
    const intervalId = window.setInterval(
      refresh,
      EXTERNAL_ORDER_SYNC_INTERVAL_MS
    );
    window.addEventListener("focus", refresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refresh);
    };
  }, [activeTab, branch?.id, refreshActiveOrders]);

  useEffect(() => {
    if (!branch?.id || !canManageWhatsApp) {
      setWhatsAppEmbeddedSignupConfig(null);
      return undefined;
    }

    let isCurrent = true;
    setWhatsAppEmbeddedSignupConfig(null);
    getExternalWhatsAppEmbeddedSignupConfig({ branchId: branch.id })
      .then((config) => {
        if (isCurrent) {
          setWhatsAppEmbeddedSignupConfig(config);
        }
      })
      .catch((configError) => {
        if (isCurrent) {
          setWhatsAppEmbeddedSignupConfig({
            ready: false,
            loadFailed: true,
            message: configError.message || "No se pudo preparar la conexion de WhatsApp. Reintenta.",
          });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [branch?.id, canManageWhatsApp, whatsAppConfigAttempt]);

  useEffect(() => {
    if (!branch?.id || restoredBranchId !== branch.id) return;

    const draft = {
      activeTab,
      selectedOrderId: selectedOrder?.id || "",
      historyDateFrom,
      historyDateTo,
      customerSearch,
      productSearch,
      selectedCategoryId,
      cartItems,
      orderCustomer,
      orderAddress,
      fulfillmentType,
      deliveryZoneId,
      deliveryFeeOverride,
      paymentMethod,
      cashReceived,
      customerNotes,
      internalNotes,
      customerForm,
      customerAddressForm,
      zoneForm,
      editingZoneId,
      distanceRateForm,
      editingDistanceRateId,
      whatsappForm,
    };

    saveDeliveryWorkspaceDraft(branch.id, draft);
  }, [
    activeTab,
    branch?.id,
    cartItems,
    cashReceived,
    customerAddressForm,
    customerForm,
    customerNotes,
    customerSearch,
    deliveryZoneId,
    deliveryFeeOverride,
    distanceRateForm,
    editingDistanceRateId,
    editingZoneId,
    fulfillmentType,
    historyDateFrom,
    historyDateTo,
    internalNotes,
    orderAddress,
    orderCustomer,
    paymentMethod,
    productSearch,
    restoredBranchId,
    selectedCategoryId,
    selectedOrder?.id,
    whatsappForm,
    zoneForm,
  ]);

  const selectedZone = useMemo(
    () => deliveryZones.find((zone) => zone.id === deliveryZoneId) || null,
    [deliveryZoneId, deliveryZones]
  );
  const selectedDistanceRate = useMemo(
    () =>
      deliveryCoverage.radiusEnabled
        ? resolveExternalDeliveryDistanceRate(
            deliveryDistanceRates,
            orderAddress.deliveryDistanceMeters
          )
        : null,
    [
      deliveryCoverage.radiusEnabled,
      deliveryDistanceRates,
      orderAddress.deliveryDistanceMeters,
    ]
  );
  const automaticDeliveryFee = useMemo(() => {
    if (fulfillmentType !== "delivery") return 0;
    if (deliveryCoverage.radiusEnabled) {
      return selectedDistanceRate
        ? Math.max(0, Number(selectedDistanceRate.flat_fee || 0))
        : null;
    }
    if (!selectedZone) return null;
    return calculateExternalCartSummary({
      items: cartItems,
      fulfillmentType,
      zone: selectedZone,
    }).deliveryFee;
  }, [
    cartItems,
    deliveryCoverage.radiusEnabled,
    fulfillmentType,
    selectedDistanceRate,
    selectedZone,
  ]);
  const effectiveDeliveryFee =
    fulfillmentType !== "delivery"
      ? 0
      : deliveryFeeOverride !== ""
        ? Math.max(0, Number(deliveryFeeOverride || 0))
        : Math.max(0, Number(automaticDeliveryFee || 0));
  const deliveryFeeHint = useMemo(() => {
    if (deliveryFeeOverride !== "") {
      return "Monto ajustado manualmente para este pedido.";
    }
    if (deliveryCoverage.radiusEnabled && selectedDistanceRate) {
      const unit = selectedDistanceRate.display_unit === "km" ? "km" : "m";
      return `${formatDistanceRateValue(
        orderAddress.deliveryDistanceMeters,
        unit
      )} de distancia · tarifa de ${formatDistanceRateValue(
        selectedDistanceRate.min_distance_meters,
        unit
      )} a ${formatDistanceRateValue(selectedDistanceRate.max_distance_meters, unit)}.`;
    }
    if (deliveryCoverage.radiusEnabled) {
      return orderAddress.deliveryDistanceMeters == null
        ? "Esta direccion no tiene distancia calculada; ingresa el envio manualmente."
        : "No hay una escala para esta distancia; ingresa el envio manualmente.";
    }
    return selectedZone
      ? `Tarifa asociada a ${selectedZone.name}. Puedes modificarla para este pedido.`
      : "Selecciona una zona para cargar la tarifa automaticamente.";
  }, [
    deliveryCoverage.radiusEnabled,
    deliveryFeeOverride,
    orderAddress.deliveryDistanceMeters,
    selectedDistanceRate,
    selectedZone,
  ]);
  const cartSummary = useMemo(
    () => {
      const summary = calculateExternalCartSummary({
        items: cartItems,
        fulfillmentType,
        zone: selectedZone,
      });
      return {
        ...summary,
        deliveryFee: effectiveDeliveryFee,
        total: summary.subtotal + effectiveDeliveryFee,
      };
    },
    [
      cartItems,
      effectiveDeliveryFee,
      fulfillmentType,
      selectedZone,
    ]
  );
  const cashSummary = useMemo(
    () => calculateExternalCashChange({ total: cartSummary.total, cashReceived }),
    [cartSummary.total, cashReceived]
  );

  const deliveryCatalogProducts = useMemo(
    () =>
      (menuSnapshot.products || []).filter(
        (product) =>
          product.active !== false && product.deliveryAvailable !== false
      ),
    [menuSnapshot.products]
  );
  const deliveryCatalogCategoryIds = useMemo(
    () =>
      new Set(
        deliveryCatalogProducts
          .map((product) => product.categoryId)
          .filter(Boolean)
      ),
    [deliveryCatalogProducts]
  );
  const deliveryCatalogCategories = useMemo(
    () =>
      (menuSnapshot.categories || []).filter((category) =>
        deliveryCatalogCategoryIds.has(category.id)
      ),
    [deliveryCatalogCategoryIds, menuSnapshot.categories]
  );

  useEffect(() => {
    if (
      selectedCategoryId &&
      !deliveryCatalogCategoryIds.has(selectedCategoryId)
    ) {
      setSelectedCategoryId("");
    }
  }, [deliveryCatalogCategoryIds, selectedCategoryId]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = productSearch.trim().toLowerCase();
    return deliveryCatalogProducts.filter((product) => {
      if (selectedCategoryId && product.categoryId !== selectedCategoryId) return false;
      if (!normalizedSearch) return true;
      return `${product.nombre || ""} ${product.descripcion || ""}`
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [
    deliveryCatalogProducts,
    productSearch,
    selectedCategoryId,
  ]);

  const filteredCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((customer) =>
      `${customer.first_name || ""} ${customer.last_name || ""} ${customer.phone_normalized || ""}`
        .toLowerCase()
        .includes(query)
    );
  }, [customerSearch, customers]);

  const showNotice = (message) => {
    setNotice(message);
    setError("");
  };

  const requirePermission = (allowed, actionDescription) => {
    if (allowed) return true;
    setError(`${currentAccessRoleLabel} no tiene permiso para ${actionDescription}.`);
    return false;
  };

  const addProductToCart = (product) => {
    if (!requirePermission(canCreateOrders, "crear pedidos")) return;
    if (product.deliveryAvailable === false) {
      setError(
        `${product.nombre || "Este plato"} no esta habilitado en el canal Delivery.`
      );
      return;
    }
    const currentItem = cartItems.find((item) => item.productId === product.id && !item.notes);
    const availability = getProductStockAvailability(
      product,
      stockEnabled,
      stockMap,
      Number(currentItem?.quantity || 0) + 1
    );
    if (!availability.available) {
      setError(availability.reason);
      return;
    }
    setCartItems((currentItems) => {
      const existing = currentItems.find((item) => item.productId === product.id && !item.notes);
      if (existing) {
        return currentItems.map((item) =>
          item === existing ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [
        ...currentItems,
        {
          productId: product.id,
          name: product.nombre || product.name || "Producto",
          unitPrice: getProductPrice(product),
          quantity: 1,
          notes: "",
          selectionDetails: [],
          stockConsumptions: buildProductStockConsumptions(product),
          imageUrl: product.fotoUrl || product.imageUrl || "",
        },
      ];
    });
  };

  const updateCartItem = (productId, patch) => {
    const currentItem = cartItems.find((item) => item.productId === productId);
    const nextQuantity = Number(patch.quantity ?? currentItem?.quantity ?? 0);
    if (currentItem && nextQuantity > 0) {
      const availability = getCartItemStockAvailability(
        currentItem,
        stockEnabled,
        stockMap,
        nextQuantity
      );
      if (!availability.available) {
        setError(availability.reason);
        return;
      }
    }
    setCartItems((currentItems) =>
      currentItems
        .map((item) => (item.productId === productId ? { ...item, ...patch } : item))
        .filter((item) => Number(item.quantity) > 0)
    );
  };

  const chooseKnownCustomer = (customer) => {
    setOrderCustomer({
      firstName: customer.first_name || "",
      lastName: customer.last_name || "",
      phone: customer.phone_original || customer.phone_normalized || "",
      email: customer.email || "",
      marketingOptIn: customer.marketing_opt_in === true,
      internalNotes: customer.internal_notes || "",
    });
    const customerAddresses = [...(customer.addresses || [])].sort((left, right) => {
      const leftBranchPriority = left.branch_id === branch?.id ? 1 : 0;
      const rightBranchPriority = right.branch_id === branch?.id ? 1 : 0;
      if (leftBranchPriority !== rightBranchPriority) {
        return rightBranchPriority - leftBranchPriority;
      }
      if (left.is_default !== right.is_default) return right.is_default ? 1 : -1;
      return new Date(right.updated_at || 0).getTime() - new Date(left.updated_at || 0).getTime();
    });
    const defaultAddress = customerAddresses[0];
    if (defaultAddress) {
      const belongsToCurrentBranch = defaultAddress.branch_id === branch?.id;
      setOrderAddress({
        label: defaultAddress.label || "Principal",
        street: defaultAddress.street || "",
        streetNumber: defaultAddress.street_number || "",
        floor: defaultAddress.floor || "",
        unit: defaultAddress.unit || "",
        neighborhood: defaultAddress.neighborhood || "",
        city: defaultAddress.city || "",
        province: defaultAddress.province || "",
        postalCode: defaultAddress.postal_code || "",
        reference: defaultAddress.reference || "",
        deliveryDistanceMeters: belongsToCurrentBranch
          ? defaultAddress.delivery_distance_meters ?? null
          : null,
        deliveryRouteDurationSeconds:
          belongsToCurrentBranch
            ? defaultAddress.delivery_route_duration_seconds ?? null
            : null,
        deliveryFeeOverride: belongsToCurrentBranch
          ? defaultAddress.delivery_fee_override ?? null
          : null,
      });
      setDeliveryZoneId(
        belongsToCurrentBranch ? defaultAddress.delivery_zone_id || "" : ""
      );
      setDeliveryFeeOverride(
        !belongsToCurrentBranch || defaultAddress.delivery_fee_override == null
          ? ""
          : String(defaultAddress.delivery_fee_override)
      );
    } else {
      setOrderAddress(EMPTY_ADDRESS);
      setDeliveryZoneId("");
      setDeliveryFeeOverride("");
    }
    showNotice(`Usamos los datos de ${customer.first_name || "este cliente"} en el pedido nuevo.`);
    setActiveTab("new");
  };

  const updateOrderAddressLocation = (patch) => {
    setOrderAddress((current) => ({
      ...current,
      ...patch,
      deliveryDistanceMeters: null,
      deliveryRouteDurationSeconds: null,
      deliveryFeeOverride: null,
    }));
    setDeliveryFeeOverride("");
  };

  const startExternalOrderPreparation = async (
    order,
    { sourceMode = "external_order" } = {}
  ) => {
    const kitchenResult = await dispatchExternalOrderToKitchen({
      branch,
      order: { ...order, status: "preparing" },
      menuSnapshot,
    });
    if (!kitchenResult.statusTransitioned) {
      await transitionExternalOrderStatus({
        orderId: order.id,
        nextStatus: "preparing",
      });
    }

    let nextStatus = "preparing";
    if (!kitchenResult.requiresKitchen) {
      nextStatus =
        order.fulfillment_type === "delivery"
          ? "ready_for_delivery"
          : "ready_for_pickup";
      await transitionExternalOrderStatus({
        orderId: order.id,
        nextStatus,
        note: "Pedido sin productos que requieran cocina.",
      });
    }

    let stockWarning = "";
    if (stockEnabled && kitchenResult.dispatched) {
      try {
        await applyOperationalOrderStock({
          branchId: branch.id,
          orderId: kitchenResult.operationalOrderId,
          sourceChannel: "external_orders",
          sourceMode,
          metadata: {
            externalOrderId: order.id,
            displayNumber: order.display_number,
          },
        });
      } catch (stockError) {
        stockWarning = ` No pudimos descontar el stock automáticamente: ${stockError.message}`;
      }
    }

    return { ...kitchenResult, nextStatus, stockWarning };
  };

  const handleCreateOrder = async () => {
    if (!requirePermission(canCreateOrders, "crear pedidos externos")) return;
    if (!orderCustomer.firstName.trim() || !orderCustomer.phone.trim()) {
      setError("El pedido necesita nombre y teléfono del cliente.");
      return;
    }
    if (!cartItems.length) {
      setError("Agrega al menos un producto antes de confirmar el pedido.");
      return;
    }
    const unavailableItem = cartItems.find((item) => {
      const product = (menuSnapshot.products || []).find(
        (candidate) => candidate.id === item.productId
      );
      return product?.deliveryAvailable === false;
    });

    if (unavailableItem) {
      setError(
        `${unavailableItem.name || "Uno de los platos"} ya no esta habilitado en el canal Delivery. Quitalo del pedido para continuar.`
      );
      return;
    }
    if (fulfillmentType === "delivery" && !deliveryZoneId) {
      setError("Selecciona una zona de delivery para calcular el envío.");
      return;
    }
    if (fulfillmentType === "delivery" && !orderAddress.street.trim()) {
      setError("Para delivery indica la calle de entrega.");
      return;
    }
    if (
      fulfillmentType === "delivery" &&
      deliveryCoverage.radiusEnabled &&
      automaticDeliveryFee == null &&
      deliveryFeeOverride === ""
    ) {
      setError(
        orderAddress.deliveryDistanceMeters == null
          ? "Esta direccion todavia no tiene una distancia calculada. Ingresa el costo de envio manualmente."
          : "No hay una tarifa configurada para esta distancia. Ingresa el costo de envio manualmente."
      );
      return;
    }
    if (!cartSummary.meetsMinimum) {
      setError(
        `Faltan ${formatCurrency(cartSummary.amountMissingForMinimum)} para llegar al mínimo de la zona.`
      );
      return;
    }
    if (paymentMethod === "cash" && cashReceived !== "" && cashSummary.missing > 0) {
      setError(`Faltan ${formatCurrency(cashSummary.missing)} para cubrir el total en efectivo.`);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const created = await createExternalOrder({
        branchId: branch.id,
        customer: orderCustomer,
        address: {
          ...orderAddress,
          deliveryFeeOverride:
            fulfillmentType === "delivery" ? cartSummary.deliveryFee : null,
        },
        deliveryZoneId: fulfillmentType === "delivery" ? deliveryZoneId : null,
        fulfillmentType,
        paymentMethod,
        cashReceived: paymentMethod === "cash" ? cashReceived : null,
        customerNotes,
        internalNotes,
        items: cartItems,
        sourceChannel: "manual",
        idempotencyKey: buildRequestKey(),
      });
      const newlyCreatedOrder = {
        id: created.id,
        display_number: created.displayNumber,
        fulfillment_type: fulfillmentType,
        payment_method: paymentMethod,
        payment_status: created.paymentStatus,
        status: created.status,
        subtotal: created.subtotal,
        delivery_fee: created.deliveryFee,
        total_amount: created.totalAmount,
        change_due: created.changeDue,
        customer: {
          first_name: orderCustomer.firstName,
          last_name: orderCustomer.lastName,
          phone_original: orderCustomer.phone,
        },
        address: fulfillmentType === "delivery" ? orderAddress : null,
        zone: selectedZone,
        customer_notes: customerNotes,
        internal_notes: internalNotes,
        created_at: new Date().toISOString(),
        items: cartItems.map((item, index) => ({
          id: `${created.id}-${index}`,
          product_id: item.productId,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          line_total: item.unitPrice * item.quantity,
          selection_details: item.selectionDetails,
          stock_consumptions: item.stockConsumptions,
          notes: item.notes,
        })),
      };
      let dispatchWarning = "";
      try {
        const kitchenResult = await startExternalOrderPreparation(newlyCreatedOrder, {
          sourceMode: "manual",
        });
        dispatchWarning = kitchenResult.stockWarning;
      } catch (dispatchError) {
        dispatchWarning = ` El pedido quedó guardado, pero requiere reintento de envío a cocina: ${dispatchError.message}`;
      }
      showNotice(
        `${created.displayNumber} quedó registrado.${dispatchWarning || " Ya entró al mismo circuito de cocina del restaurante."}`
      );
      setCartItems([]);
      setOrderCustomer(EMPTY_CUSTOMER);
      setOrderAddress(EMPTY_ADDRESS);
      setFulfillmentType("pickup");
      setDeliveryZoneId("");
      setDeliveryFeeOverride("");
      setPaymentMethod("cash");
      setCashReceived("");
      setCustomerNotes("");
      setInternalNotes("");
      setActiveTab("active");
      await loadData({ silent: true });
    } catch (createError) {
      setError(createError.message || "No se pudo crear el pedido.");
    } finally {
      setSaving(false);
    }
  };

  const handleOrderStatus = async (order, status) => {
    if (!requirePermission(canChangeStatus, "cambiar estados de pedidos")) return;
    if (status === "preparing") {
      if (!requirePermission(canDispatchToKitchen, "pasar pedidos a preparación")) return;
      setSaving(true);
      setError("");
      try {
        const kitchenResult = await startExternalOrderPreparation(order, {
          sourceMode: "admin_start_preparation",
        });
        const resultLabel = kitchenResult.requiresKitchen
          ? "entró a Cocina para comenzar la preparación"
          : "quedó listo para entregar porque no requiere cocina";
        showNotice(`${order.display_number} ${resultLabel}.${kitchenResult.stockWarning}`);
        await loadData({ silent: true });
      } catch (dispatchError) {
        setError(
          dispatchError.message ||
            "No se pudo pasar el pedido a preparación. Puedes volver a intentarlo."
        );
      } finally {
        setSaving(false);
      }
      return;
    }
    if (status === "assigned_to_driver") {
      if (!requirePermission(canAssignDelivery, "asignar repartidores")) return;
      setDriverAssignmentError("");
      setDriverAssignmentOrder(order);
      return;
    }
    setSaving(true);
    try {
      await transitionExternalOrderStatus({ orderId: order.id, nextStatus: status });
      if (["cancelled", "rejected", "refunded"].includes(status)) {
        cancelLocalWeRiderDelivery(order.id);
      }
      showNotice(`${order.display_number} pasó a ${getExternalOrderStatusMeta(status).label.toLowerCase()}.`);
      await loadData({ silent: true });
    } catch (statusError) {
      setError(statusError.message || "No se pudo actualizar el pedido.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDriverAssignment = async ({ mode, driverName, driverPhone }) => {
    if (mode === "automatic" && !WE_PEDIDOS_AVAILABLE) {
      setDriverAssignmentError(WE_PEDIDOS_UNAVAILABLE_MESSAGE);
      return;
    }
    const order = driverAssignmentOrder;
    if (!order) return;

    setSaving(true);
    setDriverAssignmentError("");
    try {
      if (isPickupWindowExpired(order)) {
        if (isUsingLocalWeRiderAuth()) {
          releaseExpiredLocalWeRiderAssignment(order.id);
        } else {
          await releaseExpiredWeRiderAssignment({ orderId: order.id });
        }
      }
      if (mode === "automatic") {
        if (isUsingLocalWeRiderAuth()) {
          publishLocalWeRiderOffer({
            order,
            branch,
            restaurantProfile: getRestaurantProfile(branch),
          });
        } else {
          await offerExternalOrderToWeRiders({ orderId: order.id });
        }
        showNotice(
          isUsingLocalWeRiderAuth()
            ? `${order.display_number} ya apareció en el Rider de prueba de localhost.`
            : `${order.display_number} fue ofrecido a los riders disponibles de We! pedidos.`
        );
      } else {
        const localOffer = removeLocalWeRiderOffer(order.id);
        if (localOffer.assigned) {
          throw new Error(
            "El Rider de prueba ya aceptó este pedido y no se puede pasar a un repartidor manual."
          );
        }
        await assignExternalOrderDriver({
          orderId: order.id,
          driverName,
          driverPhone,
        });
        showNotice(`${order.display_number} quedó asignado a ${driverName}.`);
      }
      setDriverAssignmentOrder(null);
      await loadData({ silent: true });
    } catch (assignmentError) {
      setDriverAssignmentError(
        assignmentError.message || "No se pudo asignar el repartidor."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async (order) => {
    if (!requirePermission(canCancelOrders, "cancelar pedidos")) return;
    const isPaid = String(order?.payment_status || "").toLowerCase() === "paid";
    const isMercadoPago =
      String(order?.payment_method || "").toLowerCase() === "mercado_pago";

    if (isPaid && isMercadoPago) {
      if (!requirePermission(canRefundOrders, "reintegrar cobros")) return;
      const reason = window.prompt(
        `Motivo del reintegro de ${order.display_number}:`,
        "Pedido cancelado por el restaurante"
      );
      if (reason === null) return;
      if (String(reason).trim().length < 5) {
        setError("Indica brevemente el motivo del reintegro.");
        return;
      }
      if (
        !window.confirm(
          `Se devolverá el total de ${formatCurrency(order.total_amount)} al cliente por Mercado Pago. La comisión de MozzoApp se conserva: la parte devuelta por Mercado Pago quedará como deuda del restaurante, sin duplicar lo ya registrado. Si este pago había cancelado deuda anterior, esa deuda se restablecerá. ¿Continuar?`
        )
      ) {
        return;
      }

      setSaving(true);
      setError("");
      try {
        await refundApprovedDigitalPayment({
          branchId: branch.id,
          orderId: order.id,
          reason,
        });
        cancelLocalWeRiderDelivery(order.id);
        showNotice(
          `${order.display_number} quedó reintegrado y cancelado. La comisión se conserva y el saldo pendiente del restaurante fue actualizado.`
        );
        await loadData({ silent: true });
      } catch (refundError) {
        setError(
          refundError.message ||
            "No se pudo confirmar el reintegro. El pedido no fue cancelado."
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    if (
      !window.confirm(
        isPaid
          ? `Confirma que ya devolviste manualmente el cobro de ${order.display_number}. MozzoApp marcará el pedido como reintegrado y cancelado.`
          : `¿Cancelar ${order.display_number}? Esta acción quedará auditada.`
      )
    ) {
      return;
    }

    if (isPaid) {
      setSaving(true);
      setError("");
      try {
        await recordExternalOrderPayment({
          orderId: order.id,
          paymentStatus: "refunded",
          note: "Devolución manual confirmada por el restaurante.",
        });
        await transitionExternalOrderStatus({
          orderId: order.id,
          nextStatus: "cancelled",
          note: "Pedido cancelado después de registrar la devolución manual.",
        });
        cancelLocalWeRiderDelivery(order.id);
        showNotice(`${order.display_number} quedó reintegrado y cancelado.`);
        await loadData({ silent: true });
      } catch (refundError) {
        setError(
          refundError.message || "No se pudo registrar la devolución manual."
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    await handleOrderStatus(order, "cancelled");
  };

  const handleConfirmPayment = async (order) => {
    if (!requirePermission(canEditOrders, "confirmar cobros")) return;
    setSaving(true);
    try {
      await recordExternalOrderPayment({ orderId: order.id, paymentStatus: "paid" });
      showNotice(`El cobro de ${order.display_number} quedó confirmado.`);
      await loadData({ silent: true });
    } catch (paymentError) {
      setError(paymentError.message || "No se pudo confirmar el cobro.");
    } finally {
      setSaving(false);
    }
  };

  const handleResolveCustomerRequest = async (order, resolution) => {
    if (!requirePermission(canEditOrders, "responder solicitudes de clientes")) return;
    setSaving(true);
    setError("");
    try {
      await resolveExternalOrderCustomerRequest({
        orderId: order.id,
        resolution,
        note:
          resolution === "rejected"
            ? "El restaurante indicó que no puede realizar el cambio solicitado."
            : "El restaurante revisó y atendió la solicitud del cliente.",
      });
      showNotice(
        resolution === "rejected"
          ? `La solicitud de ${order.display_number} quedó rechazada.`
          : `La solicitud de ${order.display_number} quedó atendida.`
      );
      await loadData({ silent: true });
    } catch (requestError) {
      setError(
        requestError.message || "No se pudo responder la solicitud del cliente."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomer = async () => {
    if (!requirePermission(canManageCustomers, "administrar clientes")) return;
    setSaving(true);
    try {
      const savedCustomer = await saveExternalCustomer({
        companyId,
        branchId: branch.id,
        customer: customerForm,
      });
      if (customerAddressForm.street.trim()) {
        await saveExternalCustomerAddress({
          customerId: savedCustomer.id,
          branchId: branch.id,
          address: customerAddressForm,
        });
      }
      showNotice("Cliente y dirección guardados.");
      setCustomerForm(EMPTY_CUSTOMER);
      setCustomerAddressForm(EMPTY_ADDRESS);
      await loadData({ silent: true });
    } catch (customerError) {
      setError(customerError.message || "No se pudo guardar el cliente.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCustomer = async (customer) => {
    if (!requirePermission(canManageCustomers, "eliminar clientes")) return;

    const customerName = [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") || "este cliente";
    const confirmed = window.confirm(
      `¿Quitar a ${customerName} de la cartera de clientes?\n\n` +
        "Si tiene pedidos registrados quedará archivado para conservar el historial. Si no tiene pedidos, se eliminará definitivamente."
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const result = await removeExternalCustomer({ companyId, customerId: customer.id });
      showNotice(
        result.archived
          ? "Cliente archivado. Sus pedidos históricos se conservaron."
          : "Cliente eliminado definitivamente."
      );
      await loadData({ silent: true });
    } catch (customerError) {
      setError(customerError.message || "No se pudo eliminar el cliente.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveZone = async () => {
    if (!requirePermission(canManageDelivery, "configurar zonas de delivery")) return;
    const neighborhoods = splitZoneCoverage(zoneForm.neighborhoods);
    const cities = splitZoneCoverage(zoneForm.cities);
    if (!neighborhoods.length && !cities.length && !deliveryCoverage.radiusEnabled) {
      setError(
        "Carga al menos un barrio o localidad cubierta, o activá el radio por mapa para usar una tarifa general."
      );
      return;
    }
    setSaving(true);
    try {
      await saveExternalDeliveryZone({
        branchId: branch.id,
        zone: {
          ...zoneForm,
          id: editingZoneId || undefined,
          neighborhoods,
          cities,
          allowGeneralCoverage: deliveryCoverage.radiusEnabled,
        },
      });
      showNotice("Zona de delivery guardada.");
      setZoneForm(EMPTY_ZONE);
      setEditingZoneId("");
      await loadData({ silent: true });
    } catch (zoneError) {
      setError(zoneError.message || "No se pudo guardar la zona.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDeliveryCoverage = async () => {
    if (!requirePermission(canManageDelivery, "configurar el radio de reparto")) return;

    setSaving(true);
    try {
      await saveExternalDeliveryCoverage({
        branchId: branch.id,
        radiusEnabled: deliveryCoverage.radiusEnabled === true,
        maxDistanceKm: deliveryCoverage.maxDistanceKm,
      });
      showNotice(
        deliveryCoverage.radiusEnabled
          ? "Radio de reparto guardado. Las direcciones se validarán por trayecto con Google Maps."
          : "Radio por mapa desactivado. Se seguirá validando solo por zona."
      );
      await loadData({ silent: true });
    } catch (coverageError) {
      setError(coverageError.message || "No se pudo guardar el radio de reparto.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDistanceRate = async () => {
    if (!requirePermission(canManageDelivery, "configurar tarifas por distancia")) return;

    setSaving(true);
    setError("");
    try {
      await saveExternalDeliveryDistanceRate({
        branchId: branch.id,
        rate: {
          ...distanceRateForm,
          id: editingDistanceRateId || undefined,
        },
      });
      showNotice(
        editingDistanceRateId
          ? "Tarifa por distancia actualizada."
          : "Tarifa por distancia guardada."
      );
      setDistanceRateForm(EMPTY_DISTANCE_RATE);
      setEditingDistanceRateId("");
      await loadData({ silent: true });
    } catch (rateError) {
      setError(rateError.message || "No se pudo guardar la tarifa por distancia.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDistanceRate = async (rate) => {
    if (!requirePermission(canManageDelivery, "eliminar tarifas por distancia")) return;

    const unit = rate.display_unit === "km" ? "km" : "m";
    const confirmed = window.confirm(
      `¿Eliminar el tramo de ${formatDistanceRateValue(rate.min_distance_meters, unit)} a ${formatDistanceRateValue(rate.max_distance_meters, unit)}?`
    );
    if (!confirmed) return;

    setSaving(true);
    setError("");
    try {
      await removeExternalDeliveryDistanceRate({
        branchId: branch.id,
        rateId: rate.id,
      });
      showNotice("Tarifa por distancia eliminada.");
      if (editingDistanceRateId === rate.id) {
        setDistanceRateForm(EMPTY_DISTANCE_RATE);
        setEditingDistanceRateId("");
      }
      await loadData({ silent: true });
    } catch (rateError) {
      setError(rateError.message || "No se pudo eliminar la tarifa por distancia.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChannel = async (channelCode, active) => {
    if (!requirePermission(canManageChannels, "configurar canales de venta")) return;
    setSaving(true);
    try {
      await saveExternalSalesChannel({ branchId: branch.id, channelCode, active });
      showNotice(`Canal ${getExternalSourceChannelLabel(channelCode)} ${active ? "habilitado" : "pausado"}.`);
      await loadData({ silent: true });
    } catch (channelError) {
      setError(channelError.message || "No se pudo actualizar el canal.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    if (!requirePermission(canManageWhatsApp, "configurar WhatsApp")) return;
    const hasConnectedNumber = Boolean(
      whatsAppSetup?.meta_phone_number_id && whatsAppSetup?.meta_business_account_id
    );
    if (whatsappForm.active && !hasConnectedNumber) {
      setError("Conecta primero el número de WhatsApp Business para habilitar el canal.");
      return;
    }

    setSaving(true);
    try {
      await saveExternalWhatsAppSetup({
        branchId: branch.id,
        setup: {
          ...whatsappForm,
          // The connection IDs never render in this form. They are preserved
          // from the server-side Embedded Signup result only.
          active: hasConnectedNumber ? whatsappForm.active : false,
          metaPhoneNumberId: whatsAppSetup?.meta_phone_number_id || "",
          metaBusinessAccountId: whatsAppSetup?.meta_business_account_id || "",
        },
      });
      showNotice(
        hasConnectedNumber
          ? "Configuración de WhatsApp guardada. El canal queda listo para responder Delivery."
          : "Configuración guardada. Cuando conectes WhatsApp Business, Mozzo activará este mismo canal automáticamente."
      );
      // The server now has the latest setup; refresh it instead of reapplying an older local draft.
      restoredDraftRef.current = null;
      await loadData({ silent: true });
    } catch (whatsAppError) {
      setError(whatsAppError.message || "No se pudo guardar WhatsApp.");
    } finally {
      setSaving(false);
    }
  };

  const handleConnectWhatsApp = async ({ expectedPhone, phone }) => {
    if (!requirePermission(canManageWhatsApp, "conectar WhatsApp")) return;
    if (!whatsAppEmbeddedSignupConfig?.ready) {
      setError(
        whatsAppEmbeddedSignupConfig?.message ||
          "La conexión automática de WhatsApp está siendo habilitada por Mozzo. Intenta nuevamente en unos minutos."
      );
      return;
    }

    setLoadingWhatsAppConnection(true);
    setWhatsAppConnectionPhase("authorizing");
    setSaving(true);
    setError("");
    try {
      const signup = await openWhatsAppEmbeddedSignup({ ...whatsAppEmbeddedSignupConfig, phone });
      setWhatsAppConnectionPhase("saving");
      const connection = await completeExternalWhatsAppEmbeddedSignup({
        branchId: branch.id,
        ...signup,
        expectedPhone,
      });

      setWhatsappForm((current) => ({
        ...current,
        active: true,
        displayName: connection.displayName || current.displayName,
        businessPhone: connection.businessPhone || current.businessPhone,
        webhookStatus: connection.webhookStatus || "pending",
      }));
      restoredDraftRef.current = null;
      setWhatsAppEmbeddedSignupConfig((current) => ({ ...current, connectionKind: "business_token" }));
      showNotice(
        connection.warning || "WhatsApp Business quedó vinculado. Falta enviar un mensaje al número del restaurante y comprobar la respuesta automática."
      );
      await loadData({ silent: true });
    } catch (connectionError) {
      setError(connectionError.message || "No pudimos conectar WhatsApp Business.");
    } finally {
      setLoadingWhatsAppConnection(false);
      setWhatsAppConnectionPhase("");
      setSaving(false);
    }
  };

  const handleRefreshWhatsAppActivity = async () => {
    if (!branch?.id) return;

    setSaving(true);
    setError("");
    try {
      const events = await listExternalWhatsAppWebhookEvents({ branchId: branch.id });
      setWhatsAppWebhookEvents(events);
      showNotice(
        events.some((event) => event.direction === "inbound")
          ? "Meta ya entrego mensajes entrantes a Mozzo."
          : "Todavia no llego ningun mensaje entrante desde Meta."
      );
    } catch (activityError) {
      setError(activityError.message || "No se pudo actualizar la actividad de WhatsApp.");
    } finally {
      setSaving(false);
    }
  };

  const handleSendWhatsAppTest = async (mode = "text") => {
    if (!requirePermission(canManageWhatsApp, "enviar una prueba de WhatsApp")) return;
    if (!isWhatsAppConnected) {
      setError("Conecta y activa WhatsApp Business antes de enviar la prueba.");
      return;
    }
    if (formatExternalPhone(whatsAppTestRecipient).length < 8) {
      setError("Indica un numero de WhatsApp valido con codigo de pais y area.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const result = await sendExternalWhatsAppTestMessage({
        branchId: branch.id,
        recipient: whatsAppTestRecipient,
        mode,
      });
      showNotice(
        result?.messageId
          ? `Meta acepto el mensaje de prueba. ID: ${result.messageId}`
          : "Meta acepto el mensaje de prueba."
      );
      await handleRefreshWhatsAppActivity();
    } catch (testError) {
      setError(testError.message || "No pudimos enviar la prueba de WhatsApp.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateWhatsAppTemplate = async () => {
    if (!requirePermission(canManageWhatsApp, "crear una plantilla de WhatsApp")) return;
    if (!isWhatsAppConnected) {
      setError("Conecta y activa WhatsApp Business antes de crear una plantilla.");
      return;
    }
    if (!whatsAppTemplateName.trim() || !whatsAppTemplateBody.trim()) {
      setError("Completa el nombre y el mensaje de la plantilla.");
      return;
    }

    setSaving(true);
    setError("");
    setWhatsAppTemplateResult(null);
    try {
      const result = await createExternalWhatsAppTemplate({
        branchId: branch.id,
        name: whatsAppTemplateName,
        language: "es_AR",
        body: whatsAppTemplateBody,
      });
      setWhatsAppTemplateResult(result);
      showNotice(
        `Plantilla ${result.name || whatsAppTemplateName} creada en Meta con estado ${result.status || "PENDING"}.`
      );
    } catch (templateError) {
      setError(templateError.message || "No pudimos crear la plantilla de WhatsApp.");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateDeliveryToken = () => {
    if (!requirePermission(canManageWhatsApp, "renovar el enlace Delivery")) return;
    setWhatsappForm((current) => ({
      ...current,
      deliveryPublicToken: "",
    }));
    showNotice("Al guardar se generará un nuevo enlace Delivery para esta sucursal.");
  };

  const handleCopyDeliveryPublicUrl = async () => {
    if (!requirePermission(canManageWhatsApp, "copiar el enlace Delivery")) return;
    const publicUrl = buildExternalDeliveryPublicUrl(whatsappForm.deliveryPublicToken);
    if (!publicUrl) {
      setError("Guarda el canal WhatsApp primero para generar el enlace Delivery.");
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      showNotice("Enlace Delivery copiado. Puedes enviarlo por WhatsApp o usarlo en las respuestas automáticas de Meta.");
    } catch (copyError) {
      setError("No pudimos copiar el enlace. Puedes seleccionarlo manualmente desde el campo visible.");
    }
  };

  const activeOrderCount = orders.length;
  const pendingPaymentCount = orders.filter((order) => order.payment_status !== "paid").length;
  const deliveryPublicUrl = buildExternalDeliveryPublicUrl(whatsappForm.deliveryPublicToken);
  const isWhatsAppConnected = Boolean(
    whatsAppSetup?.meta_phone_number_id && whatsAppSetup?.meta_business_account_id
  );
  const connectedWhatsAppPhone =
    whatsAppSetup?.business_phone_e164 || whatsappForm.businessPhone || "";

  if (loading) {
    return <div style={styles.page}><OperationalWorkspaceSwitcher activeWorkspace="delivery" /><div style={styles.loadingCard}>Cargando pedidos y operación…</div></div>;
  }

  return (
    <div style={styles.page}>
      <OperationalWorkspaceSwitcher activeWorkspace="delivery" />
      <section style={styles.hero}>
        <div>
          <span style={styles.eyebrow}>Operacion delivery</span>
          <h1 style={styles.title}>Ventas por delivery, retiro y canales digitales</h1>
          <p style={styles.subtitle}>
            Todo pedido queda ligado a un cliente, su forma de pago y su trazabilidad. Cuando se confirma, entra al mismo tablero de cocina que los pedidos del salón.
          </p>
        </div>
        <div style={styles.heroStats}>
          <article style={styles.statCard}><span>Activos</span><strong>{activeOrderCount}</strong><small>en la sucursal</small></article>
          <article style={styles.statCard}><span>Cobros pendientes</span><strong>{pendingPaymentCount}</strong><small>requieren control</small></article>
          <article style={styles.statCard}><span>Canales</span><strong>{salesChannels.filter((channel) => channel.active).length}</strong><small>habilitados</small></article>
        </div>
      </section>

      {error ? <div style={styles.errorBanner}>{error}</div> : null}
      {notice ? <div style={styles.noticeBanner}>{notice}<button type="button" style={styles.inlineButton} onClick={() => setNotice("")}>Ocultar</button></div> : null}

      <nav style={styles.tabRow} aria-label="Secciones de pedidos">
        {ORDER_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            disabled={tab.id === "we" && !WE_PEDIDOS_AVAILABLE}
            title={tab.id === "we" && !WE_PEDIDOS_AVAILABLE ? WE_PEDIDOS_UNAVAILABLE_MESSAGE : undefined}
            style={{ ...styles.tabButton, ...(activeTab === tab.id ? styles.tabButtonActive : {}),
              ...(tab.id === "we" && !WE_PEDIDOS_AVAILABLE ? { filter: "grayscale(1)", background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" } : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon ? <img src={tab.icon} alt="" style={styles.tabLogo} /> : null}
            {tab.label}
            {tab.id === "we" && !WE_PEDIDOS_AVAILABLE ? " - Proximamente" : ""}
          </button>
        ))}
      </nav>

      {activeTab === "active" ? (
        <section style={styles.contentGrid}>
          <div style={styles.mainCard}>
            <div style={styles.sectionHeader}>
              <div><h2 style={styles.sectionTitle}>Pedidos activos</h2><p style={styles.sectionText}>Desde aquí se controla el ciclo comercial; Cocina recibe estos pedidos en su tablero habitual.</p></div>
              <button type="button" style={styles.secondaryButton} onClick={() => loadData()} disabled={saving}>Actualizar</button>
            </div>
            {!canViewOrders ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>Puedes ver el módulo, pero no el detalle operativo de pedidos.</PermissionEmptyState> : null}
            {canViewOrders && orders.length === 0 ? <div style={styles.emptyState}>No hay pedidos externos activos en esta sucursal.</div> : null}
            {canViewOrders ? <div style={styles.orderList}>{orders.map((order) => <OrderCard key={order.id} order={order} selected={selectedOrder?.id === order.id} onSelect={setSelectedOrder} onStatus={handleOrderStatus} onCancel={handleCancelOrder} onConfirmPayment={handleConfirmPayment} canChangeStatus={canChangeStatus} canCancel={canCancelOrders} canConfirmPayment={canEditOrders} canDispatch={canDispatchToKitchen} canAssignDelivery={canAssignDelivery} kitchenMode={activeKitchenMode} saving={saving} />)}</div> : null}
          </div>
          <aside style={styles.sideCard}>
            <h2 style={styles.sectionTitle}>Control de operación</h2>
            <p style={styles.sectionText}>El detalle seleccionado muestra cliente, dirección, pago y productos antes de tomar una decisión.</p>
            {selectedOrder ? <OrderDetail order={selectedOrder} onResolveCustomerRequest={handleResolveCustomerRequest} canResolveCustomerRequest={canEditOrders} saving={saving} /> : <div style={styles.emptyState}>Selecciona un pedido para ver su detalle.</div>}
            <div style={styles.infoCallout}><strong>Auditoría activa</strong><span>Los cambios de estado y de cobro se registran en la base para consulta administrativa.</span></div>
          </aside>
        </section>
      ) : null}

      {activeTab === "history" ? (
        <section style={styles.mainCard}>
          <div style={styles.sectionHeader}><div><h2 style={styles.sectionTitle}>Historial de pedidos</h2><p style={styles.sectionText}>Consulta entregados, retiros, cancelaciones y reintegros por fecha.</p></div><button type="button" style={styles.secondaryButton} onClick={() => loadData()} disabled={saving}>Aplicar filtros</button></div>
          <div style={styles.formGridCompact}>
            <InputField label="Desde"><input type="date" style={styles.input} value={historyDateFrom} onChange={(event) => setHistoryDateFrom(event.target.value)} /></InputField>
            <InputField label="Hasta"><input type="date" style={styles.input} value={historyDateTo} onChange={(event) => setHistoryDateTo(event.target.value)} /></InputField>
            <div style={styles.field}><span style={styles.fieldLabel}>Filtro rápido</span><button type="button" style={styles.ghostButton} onClick={() => { setHistoryDateFrom(""); setHistoryDateTo(""); }}>Ver todo el historial</button></div>
          </div>
          {!canViewOrders ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>No tienes permiso para consultar el historial.</PermissionEmptyState> : null}
          {canViewOrders && historyOrders.length === 0 ? <div style={styles.emptyState}>No hay movimientos cerrados para ese período.</div> : null}
          {canViewOrders ? <div style={styles.orderList}>{historyOrders.map((order) => <OrderCard key={order.id} order={order} selected={selectedOrder?.id === order.id} onSelect={setSelectedOrder} compact />)}</div> : null}
        </section>
      ) : null}

      {activeTab === "new" ? (
        <section style={styles.newOrderLayout}>
          <div style={styles.mainCard}>
            <div style={styles.sectionHeader}><div><h2 style={styles.sectionTitle}>Nuevo pedido manual</h2><p style={styles.sectionText}>Teléfono, mostrador, mensaje o una carga interna: el precio final se valida en Supabase antes de guardar.</p></div><span style={styles.sourceBadge}>Carga manual</span></div>
            {!canCreateOrders ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>Este rol no puede crear pedidos manuales.</PermissionEmptyState> : null}
            {canCreateOrders ? <>
              <h3 style={styles.subsectionTitle}>1. Cliente</h3>
              <div style={styles.formGrid}>
                <InputField label="Nombre"><input style={styles.input} value={orderCustomer.firstName} onChange={(event) => setOrderCustomer((current) => ({ ...current, firstName: event.target.value }))} placeholder="Ej. Ana" /></InputField>
                <InputField label="Apellido"><input style={styles.input} value={orderCustomer.lastName} onChange={(event) => setOrderCustomer((current) => ({ ...current, lastName: event.target.value }))} placeholder="Opcional" /></InputField>
                <InputField label="Teléfono"><input style={styles.input} value={orderCustomer.phone} onChange={(event) => setOrderCustomer((current) => ({ ...current, phone: event.target.value }))} placeholder="Ej. 11 5555 5555" /></InputField>
                <InputField label="Email"><input type="email" style={styles.input} value={orderCustomer.email} onChange={(event) => setOrderCustomer((current) => ({ ...current, email: event.target.value }))} placeholder="Opcional" /></InputField>
              </div>
              {customers.length ? <div style={styles.knownCustomerRow}><span>¿Ya existe?</span>{customers.slice(0, 5).map((customer) => <button key={customer.id} type="button" style={styles.chipButton} onClick={() => chooseKnownCustomer(customer)}>{customer.first_name} · {formatExternalPhone(customer.phone_original)}</button>)}</div> : null}

              <h3 style={styles.subsectionTitle}>2. Modalidad y entrega</h3>
              <div style={styles.choiceRow}>
                <button type="button" style={{ ...styles.choiceButton, ...(fulfillmentType === "pickup" ? styles.choiceButtonActive : {}) }} onClick={() => setFulfillmentType("pickup")}>Retiro en el local</button>
                <button type="button" style={{ ...styles.choiceButton, ...(fulfillmentType === "delivery" ? styles.choiceButtonActive : {}) }} onClick={() => setFulfillmentType("delivery")}>Delivery</button>
              </div>
              {fulfillmentType === "delivery" ? <>
                <div style={styles.formGrid}>
                  <InputField label="Zona">
                    <select
                      style={styles.input}
                      value={deliveryZoneId}
                      onChange={(event) => {
                        setDeliveryZoneId(event.target.value);
                        setDeliveryFeeOverride("");
                      }}
                    >
                      <option value="">Selecciona una zona</option>
                      {deliveryZones
                        .filter((zone) => zone.active !== false)
                        .map((zone) => (
                          <option key={zone.id} value={zone.id}>
                            {zone.name} · {formatCurrency(zone.flat_fee)}
                          </option>
                        ))}
                    </select>
                  </InputField>
                  <InputField label="Calle">
                    <input
                      style={styles.input}
                      value={orderAddress.street}
                      onChange={(event) =>
                        updateOrderAddressLocation({ street: event.target.value })
                      }
                    />
                  </InputField>
                  <InputField label="Número">
                    <input
                      style={styles.input}
                      value={orderAddress.streetNumber}
                      onChange={(event) =>
                        updateOrderAddressLocation({ streetNumber: event.target.value })
                      }
                    />
                  </InputField>
                  <InputField label="Barrio / localidad">
                    <input
                      style={styles.input}
                      value={orderAddress.neighborhood}
                      onChange={(event) =>
                        updateOrderAddressLocation({ neighborhood: event.target.value })
                      }
                    />
                  </InputField>
                </div>
                <InputField label="Referencia para entregar"><input style={styles.input} value={orderAddress.reference} onChange={(event) => setOrderAddress((current) => ({ ...current, reference: event.target.value }))} placeholder="Portón negro, timbre 2, entre calles…" /></InputField>
              </> : <div style={styles.infoCallout}><strong>Retiro</strong><span>El cliente recibirá el pedido en el local. No se sumará costo de delivery.</span></div>}

              <h3 style={styles.subsectionTitle}>3. Productos</h3>
              <div style={styles.catalogTools}><input style={styles.input} value={productSearch} onChange={(event) => setProductSearch(event.target.value)} placeholder="Buscar producto de la carta…" /><select style={styles.input} value={selectedCategoryId} onChange={(event) => setSelectedCategoryId(event.target.value)}><option value="">Todas las categorías</option>{deliveryCatalogCategories.map((category) => <option key={category.id} value={category.id}>{category.nombre}</option>)}</select></div>
              <div style={styles.productGrid}>{filteredProducts.slice(0, 36).map((product) => { const availability = getProductStockAvailability(product, stockEnabled, stockMap); return <article key={product.id} style={styles.productCard}>{product.fotoUrl || product.imageUrl ? <img style={styles.productImage} src={product.fotoUrl || product.imageUrl} alt="" /> : <div style={styles.productImagePlaceholder}>PL</div>}<div style={styles.productInfo}><strong>{product.nombre}</strong><span>{formatCurrency(getProductPrice(product))}</span>{!availability.available ? <small style={styles.stockWarning}>{availability.reason}</small> : null}<button type="button" style={{ ...styles.addButton, ...(!availability.available ? styles.addButtonDisabled : {}) }} disabled={!availability.available} title={availability.reason} onClick={() => addProductToCart(product)}>{availability.available ? "Agregar" : "Sin stock"}</button></div></article>; })}</div>
              {!filteredProducts.length ? <div style={styles.emptyState}>No encontramos productos activos para este filtro.</div> : null}
            </> : null}
          </div>
          <aside style={styles.checkoutCard}>
            <h2 style={styles.sectionTitle}>4. Cierre del pedido</h2>
            <p style={styles.sectionText}>Elige cómo paga el cliente. Efectivo y pago inmediato dejan trazabilidad distinta.</p>
            <div style={styles.cartList}>{cartItems.length ? cartItems.map((item) => <div key={item.productId} style={styles.cartLine}><div><strong>{item.name}</strong><input style={styles.noteInput} value={item.notes} onChange={(event) => updateCartItem(item.productId, { notes: event.target.value })} placeholder="Indicaciones, ej. sin tomate" /></div><div style={styles.cartControls}><button type="button" style={styles.roundButton} onClick={() => updateCartItem(item.productId, { quantity: item.quantity - 1 })}>−</button><span>{item.quantity}</span><button type="button" style={styles.roundButton} onClick={() => updateCartItem(item.productId, { quantity: item.quantity + 1 })}>+</button></div><strong>{formatCurrency(item.unitPrice * item.quantity)}</strong></div>) : <div style={styles.emptyState}>Todavía no agregaste productos.</div>}</div>
            <div style={styles.paymentBlock}><span style={styles.fieldLabel}>¿Cómo paga?</span><div style={styles.choiceRow}><button type="button" style={{ ...styles.choiceButton, ...(paymentMethod === "cash" ? styles.choiceButtonActive : {}) }} onClick={() => setPaymentMethod("cash")}>Efectivo</button><button type="button" style={{ ...styles.choiceButton, ...(paymentMethod === "mercado_pago" ? styles.choiceButtonActive : {}) }} onClick={() => setPaymentMethod("mercado_pago")}>Pagar ahora</button><button type="button" style={{ ...styles.choiceButton, ...(paymentMethod === "cash_on_delivery" ? styles.choiceButtonActive : {}) }} onClick={() => setPaymentMethod("cash_on_delivery")}>Efectivo al entregar</button></div>
              {paymentMethod === "cash" ? <InputField label="Paga con" hint={cashReceived !== "" ? (cashSummary.change > 0 ? `Vuelto: ${formatCurrency(cashSummary.change)}` : cashSummary.missing > 0 ? `Faltan: ${formatCurrency(cashSummary.missing)}` : "Importe justo") : "Opcional si Caja confirma el monto después."}><input type="number" min="0" style={styles.input} value={cashReceived} onChange={(event) => setCashReceived(event.target.value)} placeholder="Ej. 10000" /></InputField> : null}
              {paymentMethod === "mercado_pago" ? <div style={styles.infoCallout}><strong>Pago inmediato digital</strong><span>Se registra como esperando confirmación. La integración de cobro real se enlaza al canal correspondiente sin marcar cobrado antes de tiempo.</span></div> : null}
            </div>
            {fulfillmentType === "delivery" ? (
              <div style={styles.deliveryFeeEditor}>
                <InputField label="Costo de envio" hint={deliveryFeeHint}>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    style={styles.input}
                    value={
                      deliveryFeeOverride !== ""
                        ? deliveryFeeOverride
                        : automaticDeliveryFee ?? ""
                    }
                    onChange={(event) => setDeliveryFeeOverride(event.target.value)}
                    placeholder="Ingresa el costo de envio"
                  />
                </InputField>
                {deliveryFeeOverride !== "" && automaticDeliveryFee != null ? (
                  <button
                    type="button"
                    style={styles.deliveryFeeResetButton}
                    onClick={() => setDeliveryFeeOverride("")}
                  >
                    Usar tarifa calculada
                  </button>
                ) : null}
              </div>
            ) : null}
            <InputField label="Nota del cliente"><textarea style={styles.textarea} value={customerNotes} onChange={(event) => setCustomerNotes(event.target.value)} placeholder="Ej. llamar antes de llegar" /></InputField>
            <InputField label="Nota interna"><textarea style={styles.textarea} value={internalNotes} onChange={(event) => setInternalNotes(event.target.value)} placeholder="Visible solo para el equipo" /></InputField>
            <div style={styles.totalBlock}><span>Subtotal <strong>{formatCurrency(cartSummary.subtotal)}</strong></span>{fulfillmentType === "delivery" ? <span>Envío <strong>{formatCurrency(cartSummary.deliveryFee)}</strong></span> : null}<strong style={styles.grandTotal}>Total {formatCurrency(cartSummary.total)}</strong></div>
            <button type="button" style={styles.primaryButton} disabled={saving || !canCreateOrders} onClick={handleCreateOrder}>{saving ? "Guardando…" : "Confirmar y enviar a cocina"}</button>
          </aside>
        </section>
      ) : null}

      {activeTab === "customers" ? <section style={styles.contentGrid}><div style={styles.mainCard}><div style={styles.sectionHeader}><div><h2 style={styles.sectionTitle}>Clientes</h2><p style={styles.sectionText}>Un teléfono identifica al cliente dentro de la empresa y puede tener varias direcciones.</p></div><input style={{ ...styles.input, maxWidth: 320 }} value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} placeholder="Buscar nombre o teléfono" /></div>{!canViewCustomers ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>No tienes permiso para consultar clientes.</PermissionEmptyState> : <div style={styles.customerList}>{filteredCustomers.map((customer) => <article key={customer.id} style={styles.customerCard}><div><strong>{customer.first_name} {customer.last_name}</strong><span>{formatExternalPhone(customer.phone_original)} · {customer.email || "sin email"}</span><small>{customer.orders_count || 0} pedidos · {formatCurrency(customer.total_spent)}</small></div><div style={styles.buttonRow}><button type="button" style={styles.secondaryButton} onClick={() => chooseKnownCustomer(customer)}>Usar en pedido</button>{canManageCustomers ? <button type="button" style={styles.dangerSoftButton} disabled={saving} onClick={() => handleRemoveCustomer(customer)}>Eliminar</button> : null}</div></article>)}{!filteredCustomers.length ? <div style={styles.emptyState}>Todavía no hay clientes guardados.</div> : null}</div>}</div><aside style={styles.sideCard}><h2 style={styles.sectionTitle}>Nuevo cliente</h2>{!canManageCustomers ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>No tienes permiso para editar clientes.</PermissionEmptyState> : <><InputField label="Nombre"><input style={styles.input} value={customerForm.firstName} onChange={(event) => setCustomerForm((current) => ({ ...current, firstName: event.target.value }))} /></InputField><InputField label="Apellido"><input style={styles.input} value={customerForm.lastName} onChange={(event) => setCustomerForm((current) => ({ ...current, lastName: event.target.value }))} /></InputField><InputField label="Teléfono"><input style={styles.input} value={customerForm.phone} onChange={(event) => setCustomerForm((current) => ({ ...current, phone: event.target.value }))} /></InputField><InputField label="Email"><input style={styles.input} value={customerForm.email} onChange={(event) => setCustomerForm((current) => ({ ...current, email: event.target.value }))} /></InputField><h3 style={styles.subsectionTitle}>Dirección opcional</h3><InputField label="Calle"><input style={styles.input} value={customerAddressForm.street} onChange={(event) => setCustomerAddressForm((current) => ({ ...current, street: event.target.value }))} /></InputField><InputField label="Número"><input style={styles.input} value={customerAddressForm.streetNumber} onChange={(event) => setCustomerAddressForm((current) => ({ ...current, streetNumber: event.target.value }))} /></InputField><InputField label="Barrio / localidad"><input style={styles.input} value={customerAddressForm.neighborhood} onChange={(event) => setCustomerAddressForm((current) => ({ ...current, neighborhood: event.target.value }))} /></InputField><button type="button" style={styles.primaryButton} disabled={saving} onClick={handleSaveCustomer}>Guardar cliente</button></>}</aside></section> : null}

      {activeTab === "delivery" ? (
        <section style={styles.contentGrid}>
          <div style={styles.mainCard}>
            <div style={styles.sectionHeader}>
              <div>
                <h2 style={styles.sectionTitle}>Zonas y delivery</h2>
                <p style={styles.sectionText}>
                  Las zonas definen tarifa y mínimos. El radio por mapa, si lo activas, valida la dirección real antes de aceptar el pedido.
                </p>
              </div>
            </div>
            <div style={styles.infoCallout}>
              <strong>Radio de reparto con Google Maps</strong>
              <span>
                {deliveryCoverage.originConfigured
                  ? `Origen: ${deliveryCoverage.originReference || "ubicación configurada en Mi Restaurante"}.`
                  : "Falta configurar la ubicación del local en Mi Restaurante para poder calcular trayectos."}
              </span>
              {!deliveryCoverage.available ? (
                <small>Falta aplicar el patch de cobertura por mapa en Supabase.</small>
              ) : null}
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={deliveryCoverage.radiusEnabled === true}
                  disabled={!canManageDelivery || !deliveryCoverage.available}
                  onChange={(event) =>
                    setDeliveryCoverage((current) => ({
                      ...current,
                      radiusEnabled: event.target.checked,
                    }))
                  }
                />{" "}
                Validar direcciones por distancia real
              </label>
              {deliveryCoverage.radiusEnabled ? (
                <InputField label="Radio máximo de entrega (km)" hint="Se calcula por ruta de vehículo, no en línea recta.">
                  <input
                    type="number"
                    min="0.1"
                    max="100"
                    step="0.1"
                    style={styles.input}
                    value={deliveryCoverage.maxDistanceKm}
                    disabled={!canManageDelivery || !deliveryCoverage.available}
                    onChange={(event) =>
                      setDeliveryCoverage((current) => ({
                        ...current,
                        maxDistanceKm: event.target.value,
                      }))
                    }
                    placeholder="Ej. 5"
                  />
                </InputField>
              ) : null}
              {canManageDelivery ? (
                <button
                  type="button"
                  style={styles.secondaryButton}
                  disabled={saving || !deliveryCoverage.available}
                  onClick={handleSaveDeliveryCoverage}
                >
                  Guardar radio de reparto
                </button>
              ) : null}
            </div>
            {!canManageDelivery ? (
              <PermissionEmptyState roleLabel={currentAccessRoleLabel}>
                No tienes permiso para cambiar zonas de entrega.
              </PermissionEmptyState>
            ) : deliveryCoverage.radiusEnabled ? (
              <div style={styles.orderList}>
                <div style={styles.infoCallout}>
                  <strong>Tarifas por distancia</strong>
                  <span>
                    Google Maps calcula el trayecto y Mozzo aplica automaticamente el tramo que corresponda.
                    Puedes cargar cada rango en metros o kilometros.
                  </span>
                  {!distanceRatesAvailable ? (
                    <small>
                      Falta aplicar delivery_distance_rates_phase5_patch.sql en Supabase para guardar los rangos.
                    </small>
                  ) : null}
                </div>
                {deliveryDistanceRates.map((rate) => {
                  const unit = rate.display_unit === "km" ? "km" : "m";
                  const divisor = unit === "km" ? 1000 : 1;
                  return (
                    <article key={rate.id} style={styles.zoneCard}>
                      <div>
                        <strong>
                          {formatDistanceRateValue(rate.min_distance_meters, unit)} a {formatDistanceRateValue(rate.max_distance_meters, unit)}
                        </strong>
                        <span>
                          {rate.active ? "Activo" : "Pausado"} · envio {formatCurrency(rate.flat_fee)}
                        </span>
                        <small>{rate.estimated_minutes || 35} min estimados</small>
                      </div>
                      <div style={styles.buttonRow}>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => {
                            setEditingDistanceRateId(rate.id);
                            setDistanceRateForm({
                              minDistance: String(Number(rate.min_distance_meters || 0) / divisor),
                              maxDistance: String(Number(rate.max_distance_meters || 0) / divisor),
                              unit,
                              fee: rate.flat_fee ?? "",
                              estimatedMinutes: rate.estimated_minutes ?? 35,
                              active: rate.active !== false,
                            });
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          style={styles.dangerSoftButton}
                          disabled={saving}
                          onClick={() => handleRemoveDistanceRate(rate)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </article>
                  );
                })}
                {!deliveryDistanceRates.length ? (
                  <div style={styles.emptyState}>
                    El radio esta activo, pero todavia no hay tarifas por distancia. Carga al menos un tramo para aceptar pedidos.
                  </div>
                ) : null}
              </div>
            ) : (
              <div style={styles.orderList}>
                {deliveryZones.map((zone) => (
                  <article key={zone.id} style={styles.zoneCard}>
                    <div>
                      <strong>{zone.name}</strong>
                      <span>
                        {zone.active ? "Activa" : "Pausada"} · envío {formatCurrency(zone.flat_fee)} · mínimo {formatCurrency(zone.min_order)}
                      </span>
                      <small>
                        {zone.free_from > 0 ? `Envío gratis desde ${formatCurrency(zone.free_from)}` : "Sin umbral de envío gratis"} · {zone.estimated_minutes} min estimados
                      </small>
                      <small>
                        Cubre: {formatZoneCoverage(zone.neighborhoods) || formatZoneCoverage(zone.cities) || (deliveryCoverage.radiusEnabled ? "radio por Google Maps" : "sin barrios cargados")}
                        {formatZoneCoverage(zone.neighborhoods) && formatZoneCoverage(zone.cities) ? ` · ${formatZoneCoverage(zone.cities)}` : ""}
                      </small>
                    </div>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => {
                        setEditingZoneId(zone.id);
                        setZoneForm({
                          name: zone.name || "",
                          neighborhoods: formatZoneCoverage(zone.neighborhoods),
                          cities: formatZoneCoverage(zone.cities),
                          flatFee: zone.flat_fee ?? "",
                          minOrder: zone.min_order ?? "",
                          freeFrom: zone.free_from ?? "",
                          estimatedMinutes: zone.estimated_minutes ?? 35,
                          active: zone.active !== false,
                        });
                      }}
                    >
                      Editar
                    </button>
                  </article>
                ))}
                {!deliveryZones.length ? (
                  <div style={styles.emptyState}>
                    No hay zonas activas configuradas. El Delivery público no permitirá confirmar pedidos hasta definir la cobertura.
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <aside style={styles.sideCard}>
            {deliveryCoverage.radiusEnabled ? (
              <>
                <h2 style={styles.sectionTitle}>
                  {editingDistanceRateId ? "Editar tarifa" : "Nueva tarifa por distancia"}
                </h2>
                <p style={styles.sectionText}>
                  Define desde que distancia y hasta que distancia se cobra este valor.
                </p>
                <InputField label="Unidad">
                  <select
                    style={styles.input}
                    value={distanceRateForm.unit}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({
                        ...current,
                        unit: event.target.value === "km" ? "km" : "m",
                      }))
                    }
                  >
                    <option value="m">Metros</option>
                    <option value="km">Kilometros</option>
                  </select>
                </InputField>
                <InputField label={`Desde (${distanceRateForm.unit})`}>
                  <input
                    type="number"
                    min="0"
                    step={distanceRateForm.unit === "km" ? "0.1" : "1"}
                    style={styles.input}
                    value={distanceRateForm.minDistance}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({ ...current, minDistance: event.target.value }))
                    }
                    placeholder={distanceRateForm.unit === "km" ? "Ej. 0,5" : "Ej. 500"}
                  />
                </InputField>
                <InputField label={`Hasta (${distanceRateForm.unit})`}>
                  <input
                    type="number"
                    min="0"
                    step={distanceRateForm.unit === "km" ? "0.1" : "1"}
                    style={styles.input}
                    value={distanceRateForm.maxDistance}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({ ...current, maxDistance: event.target.value }))
                    }
                    placeholder={distanceRateForm.unit === "km" ? "Ej. 1" : "Ej. 1000"}
                  />
                </InputField>
                <InputField label="Costo de envio">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={styles.input}
                    value={distanceRateForm.fee}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({ ...current, fee: event.target.value }))
                    }
                    placeholder="Ej. 1500"
                  />
                </InputField>
                <InputField label="Minutos estimados">
                  <input
                    type="number"
                    min="1"
                    max="360"
                    style={styles.input}
                    value={distanceRateForm.estimatedMinutes}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({ ...current, estimatedMinutes: event.target.value }))
                    }
                  />
                </InputField>
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={distanceRateForm.active}
                    onChange={(event) =>
                      setDistanceRateForm((current) => ({ ...current, active: event.target.checked }))
                    }
                  />{" "}
                  Tarifa disponible
                </label>
                <div style={styles.buttonRow}>
                  <button
                    type="button"
                    style={styles.primaryButton}
                    disabled={saving || !canManageDelivery || !distanceRatesAvailable}
                    onClick={handleSaveDistanceRate}
                  >
                    {editingDistanceRateId ? "Actualizar tarifa" : "Guardar tarifa"}
                  </button>
                  {editingDistanceRateId ? (
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => {
                        setDistanceRateForm(EMPTY_DISTANCE_RATE);
                        setEditingDistanceRateId("");
                      }}
                    >
                      Cancelar
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <>
            <h2 style={styles.sectionTitle}>{editingZoneId ? "Editar zona" : "Nueva zona"}</h2>
            <InputField label="Nombre">
              <input
                style={styles.input}
                value={zoneForm.name}
                onChange={(event) => setZoneForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Ej. Centro"
              />
            </InputField>
            <InputField
              label="Barrios o areas cubiertas"
              hint={
                deliveryCoverage.radiusEnabled
                  ? "Opcional: si queda vacío, esta tarifa se aplicará a toda dirección que entre en el radio por mapa."
                  : "Obligatorio. Sepáralos con coma. Ej.: Centro, Villa Cubas."
              }
            >
              <input
                style={styles.input}
                value={zoneForm.neighborhoods}
                onChange={(event) => setZoneForm((current) => ({ ...current, neighborhoods: event.target.value }))}
                placeholder="Centro, Barrio Norte"
              />
            </InputField>
            <InputField
              label="Localidades cubiertas"
              hint={
                deliveryCoverage.radiusEnabled
                  ? "Opcional con radio por mapa. Sepáralas con coma si querés documentar la cobertura."
                  : "Opcional si ya cargaste barrios. Sepáralas con coma."
              }
            >
              <input
                style={styles.input}
                value={zoneForm.cities}
                onChange={(event) => setZoneForm((current) => ({ ...current, cities: event.target.value }))}
                placeholder="San Fernando del Valle"
              />
            </InputField>
            <InputField label="Costo de envío">
              <input type="number" min="0" style={styles.input} value={zoneForm.flatFee} onChange={(event) => setZoneForm((current) => ({ ...current, flatFee: event.target.value }))} />
            </InputField>
            <InputField label="Pedido mínimo">
              <input type="number" min="0" style={styles.input} value={zoneForm.minOrder} onChange={(event) => setZoneForm((current) => ({ ...current, minOrder: event.target.value }))} />
            </InputField>
            <InputField label="Envío gratis desde">
              <input type="number" min="0" style={styles.input} value={zoneForm.freeFrom} onChange={(event) => setZoneForm((current) => ({ ...current, freeFrom: event.target.value }))} />
            </InputField>
            <InputField label="Minutos estimados">
              <input type="number" min="1" style={styles.input} value={zoneForm.estimatedMinutes} onChange={(event) => setZoneForm((current) => ({ ...current, estimatedMinutes: event.target.value }))} />
            </InputField>
            <label style={styles.checkboxRow}>
              <input type="checkbox" checked={zoneForm.active} onChange={(event) => setZoneForm((current) => ({ ...current, active: event.target.checked }))} /> Zona disponible
            </label>
            <button type="button" style={styles.primaryButton} disabled={saving || !canManageDelivery} onClick={handleSaveZone}>
              {editingZoneId ? "Actualizar zona" : "Guardar zona"}
            </button>
              </>
            )}
          </aside>
        </section>
      ) : null}

      {activeTab === "channels" ? <section style={styles.mainCard}><div style={styles.sectionHeader}><div><h2 style={styles.sectionTitle}>Canales de venta</h2><p style={styles.sectionText}>Cada canal se habilita de forma explícita. La carga manual está lista hoy; los canales públicos se activan por sus siguientes fases.</p></div></div><div style={styles.channelGrid}>{CHANNEL_CARDS.map((channel) => { const configured = salesChannels.find((item) => item.channel_code === channel.code); const isActive = configured?.active === true; return <article key={channel.code} style={styles.channelCard}><div><span style={styles.sourceBadge}>{getExternalSourceChannelLabel(channel.code)}</span><h3 style={styles.channelTitle}>{channel.title}</h3><p style={styles.sectionText}>{channel.detail}</p></div><button type="button" style={isActive ? styles.dangerSoftButton : styles.primaryButton} disabled={saving || !canManageChannels} onClick={() => handleToggleChannel(channel.code, !isActive)}>{isActive ? "Pausar canal" : "Habilitar canal"}</button></article>; })}</div>{!canManageChannels ? <PermissionEmptyState roleLabel={currentAccessRoleLabel}>No puedes activar ni pausar canales de venta.</PermissionEmptyState> : null}</section> : null}

      {activeTab === "whatsapp" ? (
        <section style={styles.contentGrid}>
          <div style={styles.mainCard}>
            <h2 style={styles.sectionTitle}>WhatsApp para Delivery</h2>
            <p style={styles.sectionText}>
              El local conserva su WhatsApp Business para atender personas normalmente. Cuando alguien escribe para pedir, Mozzo identifica su teléfono y le ofrece el menú Delivery o el chatbot.
            </p>
            <div style={styles.infoCallout}>
              <strong>Flujo por cliente</strong>
              <span>Sin ficha completa: nombre, apellido y dirección. Con ficha válida: “Ver menú Delivery” o “Pedir con chatbot”. La tarifa queda guardada para el próximo pedido cuando el local confirma su zona.</span>
            </div>
            <div style={styles.whatsappPreview}>
              <strong>{whatsAppSetup?.display_name || whatsappForm.displayName || branch?.name || "Tu restaurante"}</strong>
              <span>{whatsAppSetup?.welcome_message || whatsappForm.welcomeMessage || "Hola, te ayudo a hacer tu pedido de delivery."}</span>
              <small>
                {whatsAppSetup?.webhook_status === "verified"
                  ? "Se recibieron mensajes en el número configurado."
                  : whatsAppSetup?.webhook_status === "error"
                    ? "El mensaje llegó a Mozzo, pero Meta rechazó la respuesta. Revisá la actividad para ver el motivo exacto."
                    : "Pendiente de conectar Meta: la sucursal puede seguir usando WhatsApp Business normalmente."}
              </small>
            </div>
            <div style={styles.infoCallout}>
              <div style={styles.inlineActionHeader}>
                <strong>Actividad real de Meta</strong>
                <button type="button" style={styles.secondaryButtonSmall} disabled={saving} onClick={handleRefreshWhatsAppActivity}>Actualizar actividad</button>
              </div>
              {whatsAppWebhookEvents.length ? (
                <div style={styles.webhookEventList}>
                  {whatsAppWebhookEvents.slice(0, 4).map((event) => {
                    const activity = getWhatsAppWebhookActivityCopy(event);

                    return (
                      <div
                        key={event.id}
                        style={
                          activity.tone === "error"
                            ? styles.webhookErrorEvent
                            : styles.webhookEvent
                        }
                      >
                        <span>
                          {activity.label} · {formatWebhookActivityTime(event.created_at)}
                        </span>
                        {activity.detail ? <small>Detalle de Meta: {activity.detail}</small> : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span>Sin mensajes recibidos todavia. Si escribis Hola y esto sigue vacio, Meta no esta entregando ese mensaje al webhook.</span>
              )}
            </div>
            <details>
              <summary style={styles.connectionPendingText}>Pruebas de conexión y plantillas</summary>
              <div style={styles.metaReviewTools}>
              <div style={styles.metaReviewTool}>
                <div>
                  <strong>Enviar mensaje de prueba</strong>
                  <p style={styles.sectionText}>Comprueba desde MozzoApp que el mensaje llega al WhatsApp indicado.</p>
                </div>
                <InputField label="WhatsApp destinatario">
                  <input
                    style={styles.input}
                    inputMode="tel"
                    value={whatsAppTestRecipient}
                    onChange={(event) => setWhatsAppTestRecipient(event.target.value)}
                    placeholder="Ej: 5491112345678"
                  />
                </InputField>
                <div style={styles.buttonRow}>
                  <button type="button" style={styles.primaryButton} disabled={saving || !isWhatsAppConnected} onClick={() => handleSendWhatsAppTest("text")}>Enviar prueba</button>
                  <button type="button" style={styles.secondaryButton} disabled={saving || !isWhatsAppConnected} onClick={() => handleSendWhatsAppTest("automatic_reply")}>Probar respuesta automática</button>
                </div>
              </div>
              <div style={styles.metaReviewTool}>
                <div>
                  <strong>Crear plantilla en Meta</strong>
                  <p style={styles.sectionText}>Crea una plantilla Utility en la cuenta de WhatsApp conectada. Meta define su aprobación.</p>
                </div>
                <InputField label="Nombre de la plantilla">
                  <input style={styles.input} value={whatsAppTemplateName} onChange={(event) => setWhatsAppTemplateName(event.target.value)} placeholder="mozzo_revision_123456" />
                </InputField>
                <InputField label="Mensaje (español Argentina)">
                  <textarea style={styles.textarea} value={whatsAppTemplateBody} onChange={(event) => setWhatsAppTemplateBody(event.target.value)} />
                </InputField>
                <button type="button" style={styles.primaryButton} disabled={saving || !isWhatsAppConnected} onClick={handleCreateWhatsAppTemplate}>Crear plantilla</button>
                {whatsAppTemplateResult ? (
                  <div style={styles.successCallout} role="status">
                    <strong>Plantilla creada</strong>
                    <span>{whatsAppTemplateResult.name} · {whatsAppTemplateResult.status || "PENDING"} · {whatsAppTemplateResult.language || "es_AR"}</span>
                  </div>
                ) : null}
              </div>
              </div>
            </details>
            <div style={styles.settingsGrid}>
              <article style={styles.settingCard}>
                <strong>1. Registro de cliente</strong>
                <span>Teléfono, nombre, apellido y dirección antes del primer pedido.</span>
              </article>
              <article style={styles.settingCard}>
                <strong>2. Dos accesos</strong>
                <span>Menú Delivery para elegir visualmente o chatbot para armar el pedido conversando.</span>
              </article>
              <article style={styles.settingCard}>
                <strong>3. Cobro final</strong>
                <span>El envío se suma tras validar la dirección; luego se ofrece efectivo o Mercado Pago.</span>
              </article>
            </div>
          </div>
          <aside style={styles.sideCard}>
            {!canManageWhatsApp ? (
              <PermissionEmptyState roleLabel={currentAccessRoleLabel}>No tienes permiso para configurar WhatsApp.</PermissionEmptyState>
            ) : (
              <>
                <h2 style={styles.sectionTitle}>WhatsApp Business</h2>
                <WhatsAppNumberConnection
                  key={branch?.id}
                  currentPhone={connectedWhatsAppPhone}
                  connectionKind={whatsAppEmbeddedSignupConfig?.connectionKind}
                  busy={saving || loadingWhatsAppConnection}
                  phase={whatsAppConnectionPhase}
                  ready={whatsAppEmbeddedSignupConfig?.ready}
                  message={whatsAppEmbeddedSignupConfig?.message}
                  loadFailed={whatsAppEmbeddedSignupConfig?.loadFailed}
                  sdkStatus={whatsAppSdk.status}
                  sdkError={whatsAppSdk.error}
                  onRetry={() => whatsAppEmbeddedSignupConfig?.loadFailed
                    ? setWhatsAppConfigAttempt((value) => value + 1)
                    : whatsAppSdk.retry()}
                  onConnect={handleConnectWhatsApp}
                />
                <InputField label="Nombre visible en WhatsApp">
                  <input style={styles.input} value={whatsappForm.displayName} onChange={(event) => setWhatsappForm((current) => ({ ...current, displayName: event.target.value }))} placeholder={branch?.name || "Nombre del restaurante"} />
                </InputField>
                <h3 style={styles.subsectionTitle}>Opciones que recibirá el cliente</h3>
                <label style={styles.checkboxRow}><input type="checkbox" checked={whatsappForm.deliveryMenuEnabled} onChange={(event) => setWhatsappForm((current) => ({ ...current, deliveryMenuEnabled: event.target.checked }))} /> Ver menú Delivery</label>
                <label style={styles.checkboxRow}><input type="checkbox" checked={whatsappForm.chatbotEnabled} onChange={(event) => setWhatsappForm((current) => ({ ...current, chatbotEnabled: event.target.checked }))} /> Pedir con chatbot</label>
                <label style={styles.checkboxRow}><input type="checkbox" checked={whatsappForm.registrationRequired} onChange={(event) => setWhatsappForm((current) => ({ ...current, registrationRequired: event.target.checked }))} /> Pedir datos completos antes del primer pedido</label>
                <InputField label="Mensaje de bienvenida"><textarea style={styles.textarea} value={whatsappForm.welcomeMessage} onChange={(event) => setWhatsappForm((current) => ({ ...current, welcomeMessage: event.target.value }))} /></InputField>
                <InputField label="Mensaje fuera de horario"><textarea style={styles.textarea} value={whatsappForm.unavailableMessage} onChange={(event) => setWhatsappForm((current) => ({ ...current, unavailableMessage: event.target.value }))} /></InputField>
                <InputField label="Mensaje de derivación humana"><textarea style={styles.textarea} value={whatsappForm.handoffMessage} onChange={(event) => setWhatsappForm((current) => ({ ...current, handoffMessage: event.target.value }))} /></InputField>
                <div style={styles.infoCallout}>
                  <strong>Canal público Delivery</strong>
                  <span>{deliveryPublicUrl ? "Este es el enlace que WhatsApp enviará al cliente para registrarse, ver el menú Delivery o continuar con el chatbot." : "Se generará de forma segura al guardar."}</span>
                  {deliveryPublicUrl ? <input style={styles.input} readOnly value={deliveryPublicUrl} aria-label="Enlace público de Delivery" /> : null}
                  <div style={styles.buttonRow}>
                    {deliveryPublicUrl ? <button type="button" style={styles.secondaryButton} disabled={saving} onClick={handleCopyDeliveryPublicUrl}>Copiar enlace</button> : null}
                    {deliveryPublicUrl ? <button type="button" style={styles.secondaryButton} disabled={saving} onClick={() => window.open(deliveryPublicUrl, "_blank", "noopener,noreferrer")}>Abrir Delivery</button> : null}
                    <button type="button" style={styles.secondaryButton} disabled={saving} onClick={handleRegenerateDeliveryToken}>Renovar enlace</button>
                  </div>
                </div>
                <label style={styles.checkboxRow}><input type="checkbox" disabled={!isWhatsAppConnected} checked={whatsappForm.active} onChange={(event) => setWhatsappForm((current) => ({ ...current, active: event.target.checked }))} /> Canal Delivery activo</label>
                <button type="button" style={styles.primaryButton} disabled={saving} onClick={handleSaveWhatsApp}>{saving ? "Guardando…" : "Guardar configuración"}</button>
              </>
            )}
          </aside>
        </section>
      ) : null}

      {WE_PEDIDOS_AVAILABLE && activeTab === "we" ? (
        <WePedidosAdminPanel
          branch={branch}
          menuSnapshot={menuSnapshot}
          deliveryCoverage={deliveryCoverage}
          deliveryZones={deliveryZones}
          whatsAppSetup={whatsAppSetup}
          canManage={canManageDelivery && canManageChannels}
        />
      ) : null}

      {activeTab === "settings" ? <section style={styles.mainCard}><h2 style={styles.sectionTitle}>Configuración y alcance de Fase 1</h2><p style={styles.sectionText}>La operación manual está disponible desde este módulo. Los cobros digitales reales, menú externo, chatbot público y Meta se activarán sobre esta misma base, sin duplicar clientes ni pedidos.</p><div style={styles.settingsGrid}><article style={styles.settingCard}><strong>Base de datos</strong><span>Clientes, direcciones, zonas, pedidos, pagos y auditoría.</span><small>Requiere {PHASE1_REQUIRED_MIGRATION}.</small></article><article style={styles.settingCard}><strong>Cocina</strong><span>Los pedidos confirmados se proyectan al mismo estado operativo usado por Inicio y Cocina.</span><small>No crea un tablero paralelo.</small></article><article style={styles.settingCard}><strong>Pagos</strong><span>Efectivo, efectivo al entregar y pago digital pendiente quedan separados del estado del pedido.</span><small>Nunca se marca digital como cobrado antes de confirmarlo.</small></article><article style={styles.settingCard}><strong>Seguridad</strong><span>Los importes se recalculan en Supabase y cada cambio sensible deja auditoría.</span><small>Las claves de WhatsApp no se guardan en el navegador.</small></article></div></section> : null}

      {driverAssignmentOrder ? (
        <DriverAssignmentDialog
          key={driverAssignmentOrder.id}
          order={driverAssignmentOrder}
          error={driverAssignmentError}
          saving={saving}
          onClose={() => {
            if (saving) return;
            setDriverAssignmentOrder(null);
            setDriverAssignmentError("");
          }}
          onConfirm={handleConfirmDriverAssignment}
        />
      ) : null}
    </div>
  );
}

export function DriverAssignmentDialog({ order, error, saving, onClose, onConfirm }) {
  const [mode, setMode] = useState(
    WE_PEDIDOS_AVAILABLE && order?.delivery_assignment?.status === "searching_driver" ? "automatic" : ""
  );
  const [driverName, setDriverName] = useState(order?.driver_name || "");
  const [driverPhone, setDriverPhone] = useState(order?.driver_phone || "");

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, saving]);

  const canSubmit = (WE_PEDIDOS_AVAILABLE && mode === "automatic") || (mode === "manual" && driverName.trim());

  return (
    <div
      style={styles.assignmentOverlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <section
        style={styles.assignmentDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="driver-assignment-title"
      >
        <header style={styles.assignmentHeader}>
          <div>
            <span style={styles.eyebrow}>Pedido {order.display_number}</span>
            <h2 id="driver-assignment-title" style={styles.assignmentTitle}>
              Asignar repartidor
            </h2>
            <p style={styles.sectionText}>Elige cómo se realizará esta entrega.</p>
          </div>
          <button
            type="button"
            style={styles.assignmentCloseButton}
            aria-label="Cerrar"
            title="Cerrar"
            disabled={saving}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div style={styles.assignmentModeList} aria-label="Modalidad de asignación">
          <button
            type="button"
            style={{
              ...styles.assignmentModeButton,
              ...(mode === "automatic" ? styles.assignmentModeButtonActive : {}),
              ...(!WE_PEDIDOS_AVAILABLE ? { filter: "grayscale(1)", background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" } : {}),
            }}
            disabled={!WE_PEDIDOS_AVAILABLE}
            aria-pressed={mode === "automatic"}
            onClick={() => setMode("automatic")}
          >
            <span style={styles.assignmentModeMarker} aria-hidden="true">
              {mode === "automatic" ? "✓" : "A"}
            </span>
            <span style={styles.assignmentModeCopy}>
              <strong>Automática{!WE_PEDIDOS_AVAILABLE ? " - Proximamente" : ""}</strong>
              <small>
                Publica la oferta en We! pedidos. Los riders conectados la reciben y el
                primero que la acepta queda asignado.
              </small>
            </span>
          </button>
          <button
            type="button"
            style={{
              ...styles.assignmentModeButton,
              ...(mode === "manual" ? styles.assignmentModeButtonActive : {}),
            }}
            aria-pressed={mode === "manual"}
            onClick={() => setMode("manual")}
          >
            <span style={styles.assignmentModeMarker} aria-hidden="true">
              {mode === "manual" ? "✓" : "M"}
            </span>
            <span style={styles.assignmentModeCopy}>
              <strong>Manual</strong>
              <small>
                Reserva el pedido para un repartidor propio del restaurante.
              </small>
            </span>
          </button>
        </div>

        {mode === "automatic" ? (
          <div style={styles.assignmentInfo}>
            <strong>Oferta a riders disponibles</strong>
            <span>
              El pedido aparecerá únicamente a riders conectados. Hasta que uno lo
              acepte, podrás volver aquí y cambiarlo a asignación manual.
            </span>
          </div>
        ) : null}

        {mode === "manual" ? (
          <div style={styles.assignmentForm}>
            <InputField label="Nombre del repartidor">
              <input
                style={styles.input}
                value={driverName}
                autoFocus
                maxLength={120}
                onChange={(event) => setDriverName(event.target.value)}
                placeholder="Nombre y apellido"
              />
            </InputField>
            <InputField label="Número de teléfono" hint="Opcional">
              <input
                type="tel"
                inputMode="tel"
                style={styles.input}
                value={driverPhone}
                maxLength={60}
                onChange={(event) => setDriverPhone(event.target.value)}
                placeholder="Ej. 379 412 3456"
              />
            </InputField>
          </div>
        ) : null}

        {error ? <div style={styles.assignmentError}>{error}</div> : null}

        <footer style={styles.assignmentActions}>
          <button type="button" style={styles.secondaryButton} disabled={saving} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            style={styles.primaryButton}
            disabled={saving || !canSubmit}
            onClick={() => onConfirm({ mode, driverName: driverName.trim(), driverPhone })}
          >
            {saving
              ? "Asignando…"
              : mode === "automatic"
                ? "Publicar oferta"
                : mode === "manual"
                  ? "Asignar repartidor"
                  : "Elige una opción"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function OrderCard({
  order,
  selected,
  onSelect,
  onStatus,
  onCancel,
  onConfirmPayment,
  canChangeStatus = false,
  canCancel = false,
  canConfirmPayment = false,
  canDispatch = false,
  canAssignDelivery = false,
  kitchenMode = "manual",
  saving = false,
  compact = false,
}) {
  const statusMeta = getExternalOrderStatusMeta(order.status);
  const nextActions = statusNextActions(order, { kitchenMode });
  const customerName = `${order.customer?.first_name || "Cliente"} ${order.customer?.last_name || ""}`.trim();
  return (
    <article style={{ ...styles.orderCard, ...(selected ? styles.orderCardSelected : {}) }}>
      <button type="button" style={styles.orderOpenButton} onClick={() => onSelect?.(order)}>
        <div style={styles.orderTop}><div><strong>{order.display_number}</strong><span>{customerName} · {order.fulfillment_type === "delivery" ? "Delivery" : "Retiro"}</span></div><span style={{ ...styles.statusBadge, ...statusToneStyle(statusMeta.tone) }}>{statusMeta.label}</span></div>
        <div style={styles.orderSummary}><span>{(order.items || []).map((item) => `${item.quantity}× ${item.product_name}`).join(", ") || "Sin productos"}</span><strong>{formatCurrency(order.total_amount)}</strong></div>
        <div style={styles.orderItemPreview}>
          {(order.items || []).map((item, itemIndex) => {
            const selectionDetails = getExternalItemSelectionDetails(item);
            return (
              <span key={item.id || `${order.id}-preview-${itemIndex}`} style={styles.orderItemPreviewLine}>
                <strong>{item.quantity}x {item.product_name}</strong>
                {selectionDetails.length ? <em style={styles.orderItemPreviewChange}>{selectionDetails.join(" | ")}</em> : null}
                {item.notes ? <em style={styles.orderItemPreviewNote}>Nota: {item.notes}</em> : null}
              </span>
            );
          })}
        </div>
        {order.fulfillment_type === "delivery" ? (
          <>
            <small>
              {formatExternalOrderAddress(order.address)} · Repartidor: {getDeliveryAssignmentLabel(order)}
              {order.driver_phone ? ` (${formatExternalPhone(order.driver_phone)})` : ""}
            </small>
            <PickupVerificationNotice order={order} compact />
          </>
        ) : null}
        <small>{formatOrderDate(order.created_at)} · {getExternalPaymentMethodLabel(order.payment_method)} · {getExternalPaymentStatusLabel(order.payment_status)}</small>
        {order.customer_request_status === "pending" ? (
          <span style={styles.customerRequestCardAlert}>
            {order.customer_request_type === "cancellation"
              ? "El cliente solicita cancelar"
              : "El cliente solicita modificar"}
          </span>
        ) : null}
      </button>
      {!compact ? <div style={styles.orderActions}>{order.payment_status !== "paid" && canConfirmPayment ? <button type="button" style={styles.secondaryButton} disabled={saving} onClick={() => onConfirmPayment?.(order)}>Confirmar cobro</button> : null}{nextActions.map((action) => <button key={action.status} type="button" style={styles.primaryButtonSmall} disabled={saving || !canChangeStatus || (action.status === "preparing" && !canDispatch) || (action.status === "assigned_to_driver" && !canAssignDelivery)} onClick={() => onStatus?.(order, action.status)}>{action.label}</button>)}{canCancel && !["cancelled", "delivered", "picked_up", "rejected", "refunded"].includes(order.status) ? <button type="button" style={styles.dangerSoftButton} disabled={saving} onClick={() => onCancel?.(order)}>Cancelar</button> : null}</div> : null}
    </article>
  );
}

function OrderDetail({
  order,
  onResolveCustomerRequest,
  canResolveCustomerRequest = false,
  saving = false,
}) {
  const customerName = `${order.customer?.first_name || "Cliente"} ${order.customer?.last_name || ""}`.trim();
  return (
    <div style={styles.detailStack}>
      {order.customer_request_status === "pending" ? (
        <section style={styles.customerRequestPanel}>
          <strong>
            {order.customer_request_type === "cancellation"
              ? "Solicitud de cancelación del cliente"
              : "Solicitud de modificación del cliente"}
          </strong>
          <p style={styles.customerRequestMessage}>
            {order.customer_request_message || "El cliente no agregó detalles."}
          </p>
          <small style={styles.customerRequestHelp}>
            {order.customer_request_type === "cancellation"
              ? "Para aceptarla usa Cancelar en la tarjeta del pedido. Si ya está pagado, MozzoApp seguirá el circuito de reintegro."
              : "Contacta al cliente, realiza el ajuste si todavía es posible y luego marca la solicitud como atendida."}
          </small>
          {canResolveCustomerRequest ? (
            <div style={styles.buttonRow}>
              {order.customer_request_type === "modification" ? (
                <button
                  type="button"
                  style={styles.primaryButtonSmall}
                  disabled={saving}
                  onClick={() => onResolveCustomerRequest?.(order, "resolved")}
                >
                  Marcar atendida
                </button>
              ) : null}
              <button
                type="button"
                style={styles.dangerSoftButton}
                disabled={saving}
                onClick={() => onResolveCustomerRequest?.(order, "rejected")}
              >
                No se puede realizar
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
      <div>
        <span style={styles.detailLabel}>Pedido</span>
        <strong>{order.display_number}</strong>
        <small>{formatOrderDate(order.created_at)}</small>
      </div>
      <div>
        <span style={styles.detailLabel}>Cliente</span>
        <strong>{customerName}</strong>
        <small>{formatExternalPhone(order.customer?.phone_original)}</small>
      </div>
      <div>
        <span style={styles.detailLabel}>Entrega</span>
        <strong>{order.fulfillment_type === "delivery" ? "Delivery" : "Retiro"}</strong>
        {order.fulfillment_type === "delivery" ? <small>{formatExternalOrderAddress(order.address)}</small> : null}
        {order.address?.reference ? <small>Referencia: {order.address.reference}</small> : null}
      </div>
      {order.fulfillment_type === "delivery" ? (
        <>
          <div>
            <span style={styles.detailLabel}>Repartidor</span>
            <strong>{getDeliveryAssignmentLabel(order)}</strong>
            {order.driver_phone ? <small>{formatExternalPhone(order.driver_phone)}</small> : null}
          </div>
          <PickupVerificationNotice order={order} />
        </>
      ) : null}
      <div>
        <span style={styles.detailLabel}>Productos y modificaciones</span>
        <div style={styles.detailItemList}>
          {(order.items || []).map((item, itemIndex) => {
            const selectionDetails = getExternalItemSelectionDetails(item);
            return (
              <div key={item.id || `${order.id}-detail-${itemIndex}`} style={styles.detailItem}>
                <div style={styles.detailItemHeader}>
                  <strong>{item.quantity}x {item.product_name}</strong>
                  <strong>{formatCurrency(item.line_total)}</strong>
                </div>
                {selectionDetails.length ? <small style={styles.detailItemChanges}>{selectionDetails.join(" | ")}</small> : <small>Preparacion normal</small>}
                {item.notes ? <small>Nota: {item.notes}</small> : null}
              </div>
            );
          })}
        </div>
      </div>
      {order.customer_notes || order.internal_notes ? (
        <div>
          <span style={styles.detailLabel}>Indicaciones</span>
          {order.customer_notes ? <small>Cliente: {order.customer_notes}</small> : null}
          {order.internal_notes ? <small>Interna: {order.internal_notes}</small> : null}
        </div>
      ) : null}
      <div>
        <span style={styles.detailLabel}>Pago</span>
        <strong>{getExternalPaymentMethodLabel(order.payment_method)}</strong>
        <small>{getExternalPaymentStatusLabel(order.payment_status)}</small>
      </div>
      <div style={styles.detailAmounts}>
        <span style={styles.detailAmountLine}>Productos <strong>{formatCurrency(order.subtotal)}</strong></span>
        <span style={styles.detailAmountLine}>Envio <strong>{formatCurrency(order.delivery_fee)}</strong></span>
        {Number(order.discount_amount || 0) > 0 ? <span style={styles.detailAmountLine}>Descuento <strong>-{formatCurrency(order.discount_amount)}</strong></span> : null}
      </div>
      <div style={styles.detailTotal}>Total {formatCurrency(order.total_amount)}</div>
    </div>
  );
}

const statusToneStyle = (tone) => ({
  success: { background: "#dcfce7", color: "#166534" },
  warning: { background: "#fef3c7", color: "#92400e" },
  danger: { background: "#fee2e2", color: "#b91c1c" },
  info: { background: "#dbeafe", color: "#1d4ed8" },
  muted: { background: "#e2e8f0", color: "#334155" },
}[tone] || { background: "#e2e8f0", color: "#334155" });

const styles = {
  page: { maxWidth: 1540, margin: "0 auto", padding: "30px clamp(16px, 3vw, 48px) 72px", color: "#0f2442" },
  hero: { display: "grid", gridTemplateColumns: "minmax(0, 1.65fr) minmax(310px, 1fr)", gap: 24, padding: "28px", borderRadius: 26, background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 55%, #eff6ff 100%)", border: "1px solid #d9e6f8", boxShadow: "0 18px 48px rgba(15, 36, 66, .08)" },
  eyebrow: { display: "inline-block", color: "#ea580c", fontSize: 12, fontWeight: 900, letterSpacing: ".11em", textTransform: "uppercase", marginBottom: 10 },
  title: { margin: 0, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: "clamp(30px, 4vw, 48px)", lineHeight: 1.04 },
  subtitle: { margin: "14px 0 0", maxWidth: 760, color: "#536b8c", fontSize: 17, lineHeight: 1.6 },
  heroStats: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, alignContent: "center" },
  statCard: { padding: 16, background: "rgba(255,255,255,.82)", border: "1px solid #d8e4f4", borderRadius: 18, display: "grid", gap: 5 },
  noticeBanner: { marginTop: 18, display: "flex", gap: 14, justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderRadius: 14, background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" },
  errorBanner: { marginTop: 18, padding: "14px 18px", borderRadius: 14, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
  inlineButton: { border: 0, background: "transparent", color: "inherit", cursor: "pointer", fontWeight: 800 },
  tabRow: { display: "flex", gap: 9, overflowX: "auto", padding: "22px 0 14px" },
  tabButton: { display: "inline-flex", alignItems: "center", gap: 8, flex: "0 0 auto", border: "1px solid #cbd9eb", borderRadius: 999, background: "#fff", color: "#334155", padding: "10px 15px", fontWeight: 800, cursor: "pointer" },
  tabLogo: { width: 28, height: 28, borderRadius: 6, objectFit: "cover", objectPosition: "center", background: "#fff" },
  tabButtonActive: { borderColor: "#f97316", color: "#fff", background: "#f97316", boxShadow: "0 8px 20px rgba(234,88,12,.2)" },
  contentGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1.75fr) minmax(310px, .85fr)", gap: 20 },
  newOrderLayout: { display: "grid", gridTemplateColumns: "minmax(0, 1.65fr) minmax(330px, .85fr)", gap: 20 },
  mainCard: { minWidth: 0, background: "#fff", border: "1px solid #dce7f5", borderRadius: 22, padding: "clamp(18px, 2.5vw, 30px)", boxShadow: "0 14px 40px rgba(15,36,66,.055)" },
  sideCard: { minWidth: 0, alignSelf: "start", background: "#f8fbff", border: "1px solid #dce7f5", borderRadius: 22, padding: "clamp(18px, 2.5vw, 28px)" },
  checkoutCard: { minWidth: 0, alignSelf: "start", position: "sticky", top: 18, background: "#10213e", color: "#fff", borderRadius: 22, padding: "clamp(18px, 2.5vw, 28px)", boxShadow: "0 18px 44px rgba(15,36,66,.22)" },
  sectionHeader: { display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", marginBottom: 20 },
  sectionTitle: { margin: 0, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26 },
  subsectionTitle: { margin: "28px 0 13px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20 },
  sectionText: { margin: "8px 0 0", color: "#5a7191", lineHeight: 1.55 },
  sourceBadge: { display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "7px 10px", background: "#fff0e3", color: "#c2410c", fontWeight: 800, fontSize: 12 },
  field: { display: "grid", gap: 7, minWidth: 0, marginBottom: 13 },
  fieldLabel: { color: "inherit", fontSize: 13, fontWeight: 850 },
  fieldHint: { color: "#64748b", fontSize: 12, lineHeight: 1.45 },
  input: { boxSizing: "border-box", width: "100%", minHeight: 44, border: "1px solid #cbd9eb", borderRadius: 12, padding: "10px 12px", background: "#fff", color: "#172b4d", fontSize: 15, outline: "none" },
  inputReadonly: { boxSizing: "border-box", width: "100%", minHeight: 44, border: "1px solid #d7e2f1", borderRadius: 12, padding: "10px 12px", background: "#eef4fb", color: "#475569", fontSize: 15 },
  textarea: { boxSizing: "border-box", width: "100%", minHeight: 78, resize: "vertical", border: "1px solid #cbd9eb", borderRadius: 12, padding: "10px 12px", background: "#fff", color: "#172b4d", fontSize: 14, fontFamily: "inherit" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 13px" },
  formGridCompact: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 13, alignItems: "end", marginBottom: 20 },
  choiceRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  choiceButton: { border: "1px solid #cbd9eb", borderRadius: 12, background: "#fff", color: "#334155", padding: "10px 13px", fontWeight: 800, cursor: "pointer" },
  choiceButtonActive: { background: "#eef6ff", borderColor: "#2563eb", color: "#1d4ed8" },
  knownCustomerRow: { marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", color: "#64748b", fontSize: 13 },
  chipButton: { border: "1px solid #d7e2f1", background: "#f8fbff", color: "#334155", borderRadius: 999, padding: "6px 9px", cursor: "pointer", fontSize: 12 },
  catalogTools: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(190px, .42fr)", gap: 10, marginBottom: 14 },
  productGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 11, maxHeight: 550, overflow: "auto", paddingRight: 4 },
  productCard: { overflow: "hidden", border: "1px solid #dce7f5", borderRadius: 15, background: "#fff", display: "grid", gridTemplateColumns: "70px minmax(0, 1fr)" },
  productImage: { width: 70, height: "100%", minHeight: 100, objectFit: "cover", background: "#f1f5f9" },
  productImagePlaceholder: { minHeight: 100, display: "grid", placeItems: "center", fontWeight: 900, background: "#f1f5f9", color: "#94a3b8" },
  productInfo: { padding: 10, display: "grid", gap: 7, alignContent: "center" },
  addButton: { justifySelf: "start", border: 0, background: "#10213e", color: "#fff", borderRadius: 9, padding: "7px 9px", cursor: "pointer", fontWeight: 800 },
  addButtonDisabled: { background: "#94a3b8", cursor: "not-allowed" },
  stockWarning: { color: "#b91c1c", fontSize: 11, lineHeight: 1.35 },
  cartList: { display: "grid", gap: 10, margin: "16px 0" },
  cartLine: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", gap: 10, alignItems: "center", paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,.16)" },
  cartControls: { display: "flex", alignItems: "center", gap: 7 },
  roundButton: { width: 28, height: 28, border: "1px solid rgba(255,255,255,.38)", background: "transparent", color: "#fff", borderRadius: 50, cursor: "pointer", fontWeight: 900 },
  noteInput: { boxSizing: "border-box", width: "100%", marginTop: 4, color: "#d9e7fa", background: "rgba(255,255,255,.09)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 7, padding: "6px 7px", fontSize: 12 },
  paymentBlock: { margin: "18px 0", display: "grid", gap: 10 },
  deliveryFeeEditor: { margin: "4px 0 16px", padding: 13, borderRadius: 13, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.18)" },
  deliveryFeeResetButton: { border: 0, background: "transparent", color: "#bfdbfe", padding: 0, cursor: "pointer", fontWeight: 800, fontSize: 12 },
  totalBlock: { display: "grid", gap: 8, padding: "14px 0", borderTop: "1px solid rgba(255,255,255,.2)" },
  grandTotal: { display: "flex", justifyContent: "space-between", fontSize: 21, color: "#fff" },
  primaryButton: { border: 0, width: "100%", borderRadius: 12, background: "#f97316", color: "#fff", padding: "13px 16px", fontWeight: 900, cursor: "pointer", fontSize: 15 },
  primaryButtonSmall: { border: 0, borderRadius: 10, background: "#f97316", color: "#fff", padding: "9px 11px", fontWeight: 800, cursor: "pointer" },
  secondaryButton: { border: "1px solid #c6d5e8", borderRadius: 10, background: "#fff", color: "#334155", padding: "9px 12px", fontWeight: 800, cursor: "pointer" },
  buttonRow: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  ghostButton: { border: "1px solid #c6d5e8", borderRadius: 10, background: "#fff", color: "#334155", padding: "11px 12px", fontWeight: 800, cursor: "pointer" },
  dangerSoftButton: { border: "1px solid #fecaca", borderRadius: 10, background: "#fff5f5", color: "#b91c1c", padding: "9px 11px", fontWeight: 800, cursor: "pointer" },
  customerRequestCardAlert: { display: "inline-flex", alignSelf: "flex-start", marginTop: 4, padding: "7px 9px", borderRadius: 7, border: "1px solid #e7b85f", color: "#6f4306", background: "#fff7df", fontSize: 12, fontWeight: 900 },
  customerRequestPanel: { display: "grid", gap: 10, padding: 15, borderRadius: 10, border: "2px solid #dfad4d", color: "#5d3705", background: "#fff7df" },
  customerRequestMessage: { margin: 0, color: "#4e350f", lineHeight: 1.5, fontWeight: 700 },
  customerRequestHelp: { color: "#725522", lineHeight: 1.45 },
  infoCallout: { margin: "14px 0", padding: "13px", borderRadius: 13, background: "#eff6ff", color: "#1e3a5f", border: "1px solid #bfdbfe", display: "grid", gap: 5, lineHeight: 1.45, fontSize: 13 },
  inlineActionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  secondaryButtonSmall: { border: "1px solid #bfdbfe", background: "#fff", color: "#1d4ed8", borderRadius: 10, padding: "8px 11px", fontWeight: 800, cursor: "pointer" },
  webhookEventList: { display: "grid", gap: 7, color: "#334155", fontSize: 13, lineHeight: 1.35 },
  webhookEvent: { display: "grid", gap: 3, padding: "8px 9px", borderRadius: 9, background: "#fff" },
  webhookErrorEvent: { display: "grid", gap: 4, padding: "8px 9px", borderRadius: 9, background: "#fff1f2", color: "#9f1239" },
  emptyState: { marginTop: 12, padding: "18px", border: "1px dashed #bfd0e8", borderRadius: 14, color: "#64748b", background: "#fbfdff", lineHeight: 1.55 },
  permissionEmpty: { display: "grid", gap: 6, marginTop: 14, padding: "18px", borderRadius: 14, color: "#7c2d12", background: "#fff7ed", border: "1px solid #fed7aa" },
  orderList: { display: "grid", gap: 12 },
  orderCard: { border: "1px solid #dbe6f4", borderRadius: 15, overflow: "hidden", background: "#fff" },
  orderCardSelected: { borderColor: "#2563eb", boxShadow: "0 0 0 3px rgba(37,99,235,.11)" },
  orderOpenButton: { width: "100%", border: 0, background: "transparent", color: "inherit", padding: 15, textAlign: "left", cursor: "pointer" },
  orderTop: { display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start" },
  orderSummary: { margin: "11px 0 7px", display: "flex", gap: 16, justifyContent: "space-between", color: "#334155" },
  orderItemPreview: { display: "grid", gap: 6, margin: "0 0 9px" },
  orderItemPreviewLine: { display: "grid", gap: 3, padding: "8px 10px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" },
  orderItemPreviewChange: { color: "#b42318", fontSize: 12, fontWeight: 900, fontStyle: "normal" },
  orderItemPreviewNote: { color: "#7c2d12", fontSize: 12, fontWeight: 800, fontStyle: "normal" },
  pickupSecurity: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginTop: 10, padding: "12px 14px", borderRadius: 10, border: "1px solid #86efac", background: "#f0fdf4", color: "#14532d" },
  pickupSecurityCompact: { alignItems: "flex-start", padding: "10px 12px", flexWrap: "wrap" },
  pickupSecurityDanger: { borderColor: "#fca5a5", background: "#fef2f2", color: "#991b1b" },
  pickupSecurityLabel: { display: "block", marginBottom: 3, fontSize: 11, fontWeight: 900, textTransform: "uppercase" },
  pickupSecurityCode: { display: "block", fontSize: 25, fontWeight: 950, lineHeight: 1, letterSpacing: 0 },
  pickupSecurityPending: { display: "block", fontSize: 14, fontWeight: 900, lineHeight: 1.2, letterSpacing: 0 },
  pickupSecurityText: { maxWidth: 330, lineHeight: 1.4, fontWeight: 700 },
  statusBadge: { display: "inline-flex", flex: "0 0 auto", borderRadius: 999, padding: "5px 8px", fontWeight: 850, fontSize: 11 },
  orderActions: { display: "flex", flexWrap: "wrap", gap: 8, padding: "0 15px 15px" },
  sideCardText: { color: "#64748b" },
  detailStack: { display: "grid", gap: 13, marginTop: 16 },
  detailLabel: { display: "block", color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 3 },
  detailItemList: { display: "grid", gap: 8, marginTop: 7 },
  detailItem: { display: "grid", gap: 4, padding: 10, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" },
  detailItemHeader: { display: "flex", justifyContent: "space-between", gap: 12 },
  detailItemChanges: { color: "#b42318", fontWeight: 900 },
  detailAmounts: { display: "grid", gap: 6, paddingTop: 10, borderTop: "1px solid #dce7f5" },
  detailAmountLine: { display: "flex", justifyContent: "space-between", gap: 12 },
  detailTotal: { paddingTop: 12, borderTop: "1px solid #dce7f5", fontSize: 20, fontWeight: 900 },
  customerList: { display: "grid", gap: 10 },
  customerCard: { display: "flex", alignItems: "center", gap: 14, justifyContent: "space-between", padding: 15, border: "1px solid #dce7f5", borderRadius: 14 },
  zoneCard: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: 16, border: "1px solid #dce7f5", borderRadius: 14 },
  checkboxRow: { display: "flex", gap: 9, alignItems: "center", margin: "10px 0 16px", fontSize: 14, fontWeight: 700 },
  channelGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 14 },
  channelCard: { display: "grid", gap: 18, alignContent: "space-between", minHeight: 210, padding: 19, border: "1px solid #dce7f5", borderRadius: 18, background: "#fbfdff" },
  channelTitle: { margin: "12px 0 0", fontSize: 20 },
  whatsappPreview: { display: "grid", gap: 8, marginTop: 18, maxWidth: 520, padding: 20, borderRadius: 18, background: "#dcfce7", color: "#14532d", border: "1px solid #bbf7d0" },
  whatsappConnectCard: { display: "grid", gap: 13, margin: "18px 0 22px", padding: 17, borderRadius: 16, background: "linear-gradient(135deg, #ecfdf5 0%, #eff6ff 100%)", border: "1px solid #bbf7d0" },
  whatsappConnectCopy: { display: "grid", gap: 6, color: "#14532d", lineHeight: 1.45 },
  metaReviewTools: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginTop: 18 },
  metaReviewTool: { display: "grid", alignContent: "start", gap: 13, padding: 18, borderRadius: 8, border: "1px solid #cbd5e1", background: "#ffffff" },
  successCallout: { display: "grid", gap: 4, padding: 12, borderRadius: 8, border: "1px solid #86efac", background: "#f0fdf4", color: "#166534" },
  connectionPendingText: { color: "#526887", lineHeight: 1.45 },
  settingsGrid: { marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 13 },
  settingCard: { display: "grid", gap: 8, minHeight: 155, padding: 18, borderRadius: 16, border: "1px solid #dce7f5", background: "#fbfdff" },
  assignmentOverlay: { position: "fixed", inset: 0, zIndex: 10020, display: "grid", placeItems: "center", padding: 18, background: "rgba(15, 23, 42, .58)", overflowY: "auto" },
  assignmentDialog: { width: "min(620px, 100%)", maxHeight: "calc(100vh - 36px)", overflowY: "auto", padding: "clamp(20px, 4vw, 30px)", borderRadius: 18, background: "#fff", border: "1px solid #dce7f5", boxShadow: "0 24px 70px rgba(15, 23, 42, .28)" },
  assignmentHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 18 },
  assignmentTitle: { margin: 0, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 30 },
  assignmentCloseButton: { flex: "0 0 auto", width: 42, height: 42, display: "grid", placeItems: "center", borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", fontSize: 28, lineHeight: 1, cursor: "pointer" },
  assignmentModeList: { display: "grid", gap: 10, marginTop: 24 },
  assignmentModeButton: { width: "100%", minHeight: 82, display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, border: "1px solid #cbd5e1", background: "#fff", color: "#0f2442", textAlign: "left", cursor: "pointer" },
  assignmentModeButtonActive: { borderColor: "#f97316", background: "#fff7ed", boxShadow: "0 0 0 3px rgba(249, 115, 22, .12)" },
  assignmentModeMarker: { flex: "0 0 auto", width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: "50%", background: "#0f2442", color: "#fff", fontWeight: 900 },
  assignmentModeCopy: { minWidth: 0, display: "grid", gap: 4, lineHeight: 1.4 },
  assignmentInfo: { display: "grid", gap: 6, marginTop: 16, padding: 14, borderRadius: 11, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", lineHeight: 1.45 },
  assignmentForm: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 18 },
  assignmentError: { marginTop: 16, padding: 12, borderRadius: 10, border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c", fontWeight: 700, lineHeight: 1.45 },
  assignmentActions: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 22, paddingTop: 18, borderTop: "1px solid #e2e8f0" },
  loadingCard: { margin: 42, padding: 28, textAlign: "center", border: "1px solid #dce7f5", borderRadius: 20, background: "#fff" },
};
